import React, { useState, useEffect, useRef } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import MenuModal from "../modals/MenuModal";
import FilterModal from "../modals/FilterModal";
import "./PlacesHeader.css";
import { useDispatch } from "react-redux";
import { slice1Actions } from "../../store/reducer";
import { useSelector } from "react-redux";
import Common, {
  SortByEnum,
  API_BASE_URL,
  AICategories as DEFAULT_AI_CATEGORIES,
  AIBaseCategories as DEFAULT_AI_BASE_CATEGORIES,
} from "../../Common/Common";
import Places from "../../Models/Places";
import { getFeaturedPlaceMatch } from "../../Common/FeaturedPlaceImages";

import AlertModal from "../modals/AlertModal";
import PromptModal from "../modals/PromptModal";
import GroupsDialog from "../modals/GroupsDialog";
import CategoriesDialog from "../modals/CategoriesDialog";
import Appointment from "../../Models/Event";
import { DateTime } from "luxon";
import SectionsDialog from "../modals/SectionsDialog";
import { countries } from "../../Data/Countries";
import { citiesByCountry } from "../../Data/Cities";
import Place, { Place_Source } from "../../Models/Place";
import Schedule from "../../Models/Schedule";
import GooglePlaces, { mapGoogleTypesToCategoryId } from "../../Models/GooglePlaces";
import "../modals/GeoCharts.css";
import { searchRadiusKm } from "../../Common/placeSize";
import PlaceMenu from "../../CommonWeb/PlaceMenu";
import WikiMedia from "../../Models/Wikimedia";
import Flickr from "../../Models/Flickr";
// import Mapillary from "../../Models/Mapillary"; // re-enable once REACT_APP_MAPILLARY_ACCESS_TOKEN is set up
import ImageDialog from "../modals/ImageDialog";
import GoogleDrivePicker from "../modals/GoogleDrivePicker";
import PersonalizeGuideReviewModal from "../modals/PersonalizeGuideReviewModal";
import IdentifyPlacesModal from "../modals/IdentifyPlacesModal";
import { isWebPhoto } from "../../Common/photoSource";
import { removePlaceFromEvents } from "../../Common/identifyPlaces";
import BuildScheduleDialog from "../modals/BuildScheduleDialog";
import { placeRowFor } from "../../Common/placeRow";
import { isHotel, hotelCategoryIds } from "../../Common/dayStartTravel";
import { takeDailyLimitNotice, clearDailyLimitNotice, showLimitNotice } from "../../Common/GateLimit";
import PlaceEditModal from "../modals/PlaceEditModal";
import AISearch from "../modals/AISearch";
// Places menu actions whose place list shows each place's photo, category
// and notes (Common/placeRow.js) — the ones where you choose places.
const PLACE_DETAIL_OPERATIONS = ["BuildSchedule", "Favorite", "Visited", "MustSee", "BucketList", "Hidden", "Delete Places", "Groups", "Categories"];

const PlacesHeader = ({ setSearch, setSortBy, handleSortSubmit }) => {
  const [shouldFilter, setShouldFilter] = useState(false);
  const dispatch = useDispatch();
  let SortBy = useSelector((state) => state.SortBy);
  let noFiltering = useSelector((state) => state.noFiltering);
  let SearchText = useSelector((state) => state.SearchText);
  let MobileMode = useSelector((state) => state.MobileMode);
  let CurrentAppScreen = useSelector((state) => state.CurrentAppScreen);
  let AppScreenStack = useSelector((state) => state.AppScreenStack);
  let GuidesOrPlaces = useSelector((state) => state.GuidesOrPlaces);
  let ScheduleName = useSelector((state) => state.ScheduleName);
  // Trip replay (2026-09-05) — the actual engine (timer, map panning, the
  // tooltip) lives in Map.js, a separate component with no direct prop/ref
  // path here; these buttons are a remote control that just flips
  // ReplayState in Redux, same pattern as centerMapToPlaces/
  // HighlightedPlaces elsewhere in this app.
  let ReplayState = useSelector((state) => state.ReplayState);
  let GuideEditMode = useSelector((state) => state.GuideEditMode);
  let showHiddenPlace = useSelector((state) => state.showHiddenPlace);
  let GuideSettings = useSelector((state) => state.GuideSettings);
  let GuideFormat = useSelector((state) => state.GuideFormat);

  const [MenuModalShow, setMenuModalShow] = useState(false);
  const [MenuModalImage, setMenuModalImage] = useState(null);
  const [MenuModalList, setMenuModalList] = useState([]);
  const [MenuModalHeading, setMenuModalHeading] = useState("");
  const [MenuModalPlace, setMenuModalPlace] = useState(null);
  const [MenuModalInfo, setMenuModalInfo] = useState("");
  const [MenuModalBackFn, setMenuModalBackFn] = useState(null);
  let user_info = useSelector((state) => state.user_info);
  let AppSettings = useSelector((state) => state.AppSettings);
  let SearchResultsPlacesList = useSelector((state) => state.SearchResultsPlacesList);
  const advancedMode = user_info?.advanced_mode === "On";
  const [AISearchShow, setAISearchShow] = useState(false);

  // Lets the mobile-mode command palette (MobileBottomNavBar.js) open this
  // component's own Places Menu after navigating here, since the palette
  // can't call this component's local functions directly.
  const placesMenuRef = useRef(null);
  useEffect(() => {
    placesMenuRef.current = EditMenu;
  });

  // Lets each menu's own "N more options in Advanced Mode" hint reopen the
  // same menu after toggling, so the newly-revealed items are immediately
  // visible instead of the user having to reopen it manually. Refreshed
  // every render (not just when the menu opens) so the ref always points at
  // a closure that sees the just-updated advancedMode value.
  const editContentMenuRef = useRef(null);
  useEffect(() => {
    editContentMenuRef.current = EditContentMenu;
  });

  useEffect(() => {
    const handler = () => { if (placesMenuRef.current) placesMenuRef.current(); };
    window.addEventListener("openPlacesMenu", handler);
    return () => window.removeEventListener("openPlacesMenu", handler);
  }, []);

  // "Import Itinerary from Image" hands its just-created places straight to
  // Build Trip from Places (the Places menu item), instead of only opening
  // the menu. Assigned each render so it always calls the current handler.
  const buildFromPlacesRef = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (buildFromPlacesRef.current) buildFromPlacesRef.current(e?.detail?.placeIds); };
    window.addEventListener("andiamo-build-trip-from-places", handler);
    return () => window.removeEventListener("andiamo-build-trip-from-places", handler);
  }, []);

  // Lets "Create Journal from Photos" (HomeHeader.js / MobileBottomNavBar.js)
  // deep-link into this component's "Personalize Guide with Photos" modal for
  // guides that already have places, since it can't call this component's
  // local functions directly.
  useEffect(() => {
    const handler = () => setPersonalizeShow(true);
    window.addEventListener("openPersonalizeGuideWithPhotos", handler);
    return () => window.removeEventListener("openPersonalizeGuideWithPhotos", handler);
  }, []);

  const [PromptModalShow, setPromptModalShow] = useState(false);
  const [PromptModalTitle, setPromptModalTitle] = useState("Enter Value");
  const [PromptModalMessage, setPromptModalMessage] = useState("");
  const [PromptModalButtons, setPromptModalButtons] = useState([]);
  const [PromptModalInputs, setPromptModalInputs] = useState([]);
  const [addPlaceShow, setAddPlaceShow] = useState(false);
  const [addPlaceName, setAddPlaceName] = useState("");
  const [addPlaceCountry, setAddPlaceCountry] = useState([...countries].sort()[0] || "");
  // "select" shows a dropdown of the country's curated cities (when any
  // exist); "type" shows the original free-text input — reached via the
  // dropdown's own "Type a different city…" option, or automatically for a
  // country with no curated cities.
  const [addPlaceCityMode, setAddPlaceCityMode] = useState("select");
  const [addPlaceVisited, setAddPlaceVisited] = useState(false);
  const [addPlaceError, setAddPlaceError] = useState("");
  const [addPlaceSaving, setAddPlaceSaving] = useState(false);
  const [addPlacesBulkShow, setAddPlacesBulkShow] = useState(false);
  const [addPlacesBulkText, setAddPlacesBulkText] = useState("");
  const [addPlacesBulkGroup, setAddPlacesBulkGroup] = useState("");
  const [addPlacesBulkVisited, setAddPlacesBulkVisited] = useState(false);
  const [addPlacesBulkProcessing, setAddPlacesBulkProcessing] = useState(false);
  const [addPlacesBulkProgress, setAddPlacesBulkProgress] = useState("");
  const [addPlacesBulkErrorNote, setAddPlacesBulkErrorNote] = useState("");
  const [AlertModalShow, setAlertModalShow] = useState(false);
  const [AlertModalTitle, setAlertModalTitle] = useState("");
  const [AlertModalMessage, setAlertModalMessage] = useState("");
  const [AlertModalButtons, setAlertModalButtons] = useState([]);
  const [photoSearchLoading, setPhotoSearchLoading] = useState(false);
  const [photoSearchProgress, setPhotoSearchProgress] = useState("");

  // "Personalize Guide with Photos" — unifies what used to be three
  // separate features (Use My Photos, Add Photos to Places, and Map.js's
  // Search places from Photos) into one matching pass + one review modal.
  // See PersonalizeGuideReviewModal.js.
  const [personalizeShow, setPersonalizeShow] = useState(false);
  // "Identify Places" (4.10.55): name existing places from their location.
  const [identifyPlacesShow, setIdentifyPlacesShow] = useState(false);
  useEffect(() => {
    const handler = () => setIdentifyPlacesShow(true);
    window.addEventListener("openIdentifyPlaces", handler);
    return () => window.removeEventListener("openIdentifyPlaces", handler);
  }, []);
  const [buildScheduleDialogShow, setBuildScheduleDialogShow] = useState(false);
  const [buildScheduleDefaultName, setBuildScheduleDefaultName] = useState("Trip");
  const [buildScheduleDefaultDate, setBuildScheduleDefaultDate] = useState(new Date());
  const [buildScheduleDefaultEndDate, setBuildScheduleDefaultEndDate] = useState(new Date());
  const [buildScheduleDestinationSummary, setBuildScheduleDestinationSummary] = useState([]);
  const buildSchedulePlacesRef = useRef([]);
  const [personalizeSource, setPersonalizeSource] = useState("device");
  const [personalizeReviewShow, setPersonalizeReviewShow] = useState(false);
  const [personalizeMatchedItems, setPersonalizeMatchedItems] = useState([]);
  const [personalizeNewClusters, setPersonalizeNewClusters] = useState([]);
  const [personalizeSensitivity, setPersonalizeSensitivity] = useState(200);
  const [personalizeMaxSpan, setPersonalizeMaxSpan] = useState(1000);
  // 0 = off (default) — an independent floor under "new place" candidacy,
  // separate from personalizeSensitivity (which only controls the
  // match-to-an-existing-place / photo-clustering distance, capped at
  // 1km). Lets excursions to a nearby town surface as their own new places
  // instead of getting buried among near-duplicate shots of places already
  // in the guide, without having to loosen the clustering distance itself.
  const [personalizeMinNewPlaceDistance, setPersonalizeMinNewPlaceDistance] = useState(0);
  const [personalizeUnmatchedPlaces, setPersonalizeUnmatchedPlaces] = useState([]);
  // Places with a web photo and none of the person's photos within the match
  // distance, with their nearest photos within 1 km — shown only by the review's
  // "Only places whose photo is from the web" (2026-10-28).
  const [personalizeWebExtras, setPersonalizeWebExtras] = useState([]);
  const personalizeGeoPhotosRef = useRef([]);
  const [ImageDialogShow, setImageDialogShow] = useState(false);
  const [googleDrivePickerShow, setGoogleDrivePickerShow] = useState(false);
  const [infoSearchLoading, setInfoSearchLoading] = useState(false);
  const [infoSearchProgress, setInfoSearchProgress] = useState("");
  const [openingHoursSearchLoading, setOpeningHoursSearchLoading] = useState(false);
  const [openingHoursSearchProgress, setOpeningHoursSearchProgress] = useState("");
  const [countrySearchLoading, setCountrySearchLoading] = useState(false);
  const [countrySearchProgress, setCountrySearchProgress] = useState("");
  const [categorySearchLoading, setCategorySearchLoading] = useState(false);
  const [categorySearchProgress, setCategorySearchProgress] = useState("");

  const [GroupsDialogShow, setGroupsDialogShow] = useState(false);
  const [CategoriesDialogShow, setCategoriesDialogShow] = useState(false);
  const [SectionsDialogShow, setSectionsDialogShow] = useState(false);
  let filtered_places = useSelector((state) => state.places);
  const [PlacesSelected] = useState([]);
  const [editPlaceShow, setEditPlaceShow] = useState(false);
  let TempValue = useSelector((state) => state.TempValue);


  const handleSearChanged = (event) => {
    //console.log(event.target.value);
    dispatch(slice1Actions.SetSearchText({ value: event.target.value }));
    dispatch(slice1Actions.setGuide({ guide: null }));
  };
  function handleSort(_SortBy) {
    switch (_SortBy) {
      case "Name":
        dispatch(slice1Actions.SetSortBy({ value: SortByEnum.NAME }));
        dispatch(slice1Actions.setGuide({ guide: null }));
        break;
      case "Category":
        dispatch(slice1Actions.SetSortBy({ value: SortByEnum.CATEGORY }));
        dispatch(slice1Actions.setGuide({ guide: null }));
        break;
      case "Group":
        dispatch(slice1Actions.SetSortBy({ value: SortByEnum.GROUP }));
        dispatch(slice1Actions.setGuide({ guide: null }));
        break;
      case "Rating":
        dispatch(slice1Actions.SetSortBy({ value: SortByEnum.RATING }));
        dispatch(slice1Actions.setGuide({ guide: null }));
        break;
      case "Distance":
        dispatch(slice1Actions.SetSortBy({ value: SortByEnum.DISTANCE }));
        dispatch(slice1Actions.setGuide({ guide: null }));
        break;
      case "Date":
        dispatch(slice1Actions.SetSortBy({ value: SortByEnum.DATE }));
        dispatch(slice1Actions.setGuide({ guide: null }));
        break;
      default:
        break;
    }
  }

  //   const searchMenu = () => {
  //     //console.log("SortBy", SortBy);
  //     const menuList = [
  //       {
  //         label: "Attractions",
  //         method: searchAttractions,
  //         icon: <FontAwesomeIcon icon={solid("check")} />,
  //         checked: false,
  //         visible: true,
  //       },
  //       {
  //         label: "Google text search",
  //         method: googleTextSearch,
  //         icon: <FontAwesomeIcon icon={solid("check")} />,
  //         checked: false,
  //         visible: true,
  //       },
  //       // {
  //       //   label: "Google category search",
  //       //   method: googleCategorySearch,
  //       //   icon: <FontAwesomeIcon icon={solid("check")} />,
  //       //   checked: false,
  //       //   visible: true,
  //       // },
  //       // {
  //       //   label: "Wikipedia text search",
  //       //   method: wikipediaTextSearch,
  //       //   icon: <FontAwesomeIcon icon={solid("check")} />,
  //       //   checked: false,
  //       //   visible: true,
  //       // },
  //       {
  //         label: "Search in Foursquare",
  //         method: goToFoursquare,
  //         icon: <FontAwesomeIcon icon={solid("check")} />,
  //         checked: false,
  //         visible: true,
  //       },
  //       {
  //         label: "Search in Yelp",
  //         method: goToYelp,
  //         icon: <FontAwesomeIcon icon={solid("check")} />,
  //         checked: false,
  //         visible: true,
  //       },
  //       {
  //         label: "Search Happy Cow (vegan)",
  //         method: goToHappyCow,
  //         icon: null,
  //         visible: true,
  //       },
  //       {
  //         label: "Add all search results",
  //         method: AddAllSearchResults,
  //         icon: null,
  //         visible: SearchResultsPlacesList.length > 0,
  //       },
  //       {
  //         label: "Remove search results",
  //         method: RemoveSearchResults,
  //         icon: null,
  //         checked: false,
  //         visible: SearchResultsPlacesList.length > 0,
  //       },
  //     ];
  //     //console.log("menuList", menuList);
  //     setMenuModalList(menuList);
  //     setMenuModalHeading("Search Places Menu");
  //     setMenuModalImage(<FontAwesomeIcon icon={solid("search")} />);
  //     setMenuModalPlace(null);
  //     setMenuModalShow(true);
  //   };

  //   const RemoveSearchResults = () => {
  //     dispatch(slice1Actions.setSearchResultsPlacesList({ value: [] }));
  //     dispatch(slice1Actions.setGuide({ guide: null }));
  //   };

  //   const AddAllSearchResults = (action) => {
  //     dispatch(slice1Actions.addAllResultsToGuide({ value: null }));
  //     dispatch(slice1Actions.setGuide({ guide: null }));
  //   };

  //   const goToYelp = () => {
  //     const centerRadius = getCenterAndRadius();
  //     let action = `https://www.yelp.com/search?find_loc=${centerRadius.center.latitude},${centerRadius.center.longitude}`;
  //     let htmlString = `<!DOCTYPE html>
  // <html>
  //   <head>
  //     <title>StreetView</title>
  //     <meta http-equiv='Content-Type' content='text/html; charset=utf-8'/>
  //   </head>
  //   <body>
  //     <script type='text/javascript'>
  //       window.location.href = "${action}";
  //     </script>
  //   </body>
  // </html>`;
  //     let win = window.open("about:blank", "", "_blank");
  //     win.document.write(htmlString);
  //     win.focus();
  //   };

  //   const goToFoursquare = () => {
  //     const centerRadius = getCenterAndRadius();
  //     let action = `https://foursquare.com/explore?mode=url&ll=${centerRadius.center.latitude},${centerRadius.center.longitude}&q=Top%20Picks`;

  //     let htmlString = `<!DOCTYPE html>
  // <html>
  //   <head>
  //     <title>StreetView</title>
  //     <meta http-equiv='Content-Type' content='text/html; charset=utf-8'/>
  //   </head>
  //   <body>
  //     <script type='text/javascript'>
  //       window.location.href = "${action}";
  //     </script>
  //   </body>
  // </html>`;
  //     let win = window.open("about:blank", "", "_blank");
  //     win.document.write(htmlString);
  //     win.focus();
  //   };

  //   const goToHappyCow = () => {
  //     const centerRadius = getCenterAndRadius();
  //     const lon_min = MapBounds.west.toFixed(6);
  //     const lon_max = MapBounds.east.toFixed(6);
  //     const lat_min = MapBounds.south.toFixed(6);
  //     const lat_max = MapBounds.north.toFixed(6);
  //     const bb = `${lat_min}%2C${lon_min}%2C${lat_max}%2C${lon_max}`;

  //     const action = `https://www.happycow.net/searchmap?s=3&location=&filters=vegan-vegetarian-vegfriendly&metric=mi&limit=81&order=default&lat=${centerRadius.center.latitude}&lng=${centerRadius.center.longitude}&zoom=15&page=1&bb=${bb}`;

  //     let htmlString = `<!DOCTYPE html>
  //     <html>
  //       <head>
  //         <title>StreetView</title>
  //         <meta http-equiv='Content-Type' content='text/html; charset=utf-8'/>
  //       </head>
  //       <body>
  //         <script type='text/javascript'>
  //           window.location.href = "${action}";
  //         </script>
  //       </body>
  //     </html>`;
  //     let win = window.open("about:blank", "", "_blank");
  //     win.document.write(htmlString);
  //     win.focus();
  //   };

  //   const searchAttractions = () => {
  //     dispatch(slice1Actions.SetLoadingGuide({ value: true }));
  //     _Attractions.searchPlaces(
  //       {
  //         lon_min: MapBounds.west,
  //         lon_max: MapBounds.east,
  //         lat_min: MapBounds.south,
  //         lat_max: MapBounds.north,
  //       },
  //       "20",
  //       searchAttractionsCallback
  //     );
  //   };

  //   const searchAttractionsCallback = (attractions) => {
  //     if (attractions == null || attractions.length === 0) {
  //       //console.log("searchAttractionsCallback attractions", attractions);
  //       dispatch(slice1Actions.SetLoadingGuide({ value: false }));
  //       return;
  //     }
  //     //console.log("searchAttractionsCallback attractions.length", attractions.length);
  //     _Attractions.getDetails(attractions, getAttractionsDetailsCallback);
  //   };

  //   const getAttractionsDetailsCallback = (places) => {
  //     dispatch(slice1Actions.setSearchResultsPlacesList({ value: places }));
  //     dispatch(slice1Actions.setGuide({ guide: null }));
  //     dispatch(slice1Actions.SetLoadingGuide({ value: false }));
  //   };

  //   const googleCategorySearch = () => {
  //     const items = GooglePlaces.getGoogleCategories();
  //     // let id = 0;
  //     // items.forEach((item) => {
  //     //   item.id = (id++).toString();
  //     // });
  //     console.log("items", items);
  //     dispatch(slice1Actions.setMultiselectCallback({ value: googleCategoriesCallBack }));
  //     dispatch(slice1Actions.setMultiselectItems({ value: items }));
  //     dispatch(slice1Actions.setMultiselectSelectedItems({ value: [] }));
  //     dispatch(slice1Actions.setMultiselectMode({ value: "uni" }));
  //     dispatch(slice1Actions.setMultiselectSearchText({ value: "" }));
  //     dispatch(slice1Actions.setShowMultiselect({ value: true }));
  //     // setMultiselectItems(items);
  //     // setMultiselectSelectedItems([]);
  //     // setMultiselectMode("uni");
  //     // //setMultiselectCallback(googleCategoriesCallBack);
  //     // setMultiselectTitle("Select Category");
  //     // setMultiselectSearchText("");
  //     // setShowMultiselect(true);
  //   };

  //   const googleCategoriesCallBack = (google_categories) => {
  //     if (google_categories == null) return;
  //     if (google_categories.length === 0) return;
  //     let cats = google_categories[0].value;
  //     cats = cats.replace(/ /g, "%20");
  //     dispatch(slice1Actions.SetLoadingGuide({ value: true }));
  //     const centerRadius = getCenterAndRadius();
  //     _GooglePlaces.searchPlaces(
  //       `${centerRadius.center.latitude},${centerRadius.center.longitude}`,
  //       centerRadius.radius,
  //       cats,
  //       null,
  //       searchGooglePlaceCallback
  //     );
  //   };
  //   const googleTextSearch = () => {
  //     setPromptModalShow(true);
  //     setPromptModalTitle("Search");
  //     setPromptModalMessage("Google places");
  //     setPromptModalButtons([
  //       { Title: "OK", Method: _googleTextSearch },
  //       { Title: "CANCEL", Method: cancelSearch },
  //     ]);
  //     setPromptModalInputs([{ name: "Search Text", value: SavedTextSearch }]);
  //   };

  //   const cancelSearch = () => {
  //     dispatch(slice1Actions.SetLoadingGuide({ value: false }));
  //   };

  //   const getCenterAndRadius = () => {
  //     const distance_lat =
  //       Places.calculateDistance(
  //         {
  //           latitude: MapBounds.south,
  //           longitude: MapBounds.west,
  //         },
  //         {
  //           latitude: MapBounds.north,
  //           longitude: MapBounds.east,
  //         }
  //       ) * 1000;
  //     const distance_lon =
  //       Places.calculateDistance(
  //         {
  //           latitude: MapBounds.south,
  //           longitude: MapBounds.west,
  //         },
  //         {
  //           latitude: MapBounds.north,
  //           longitude: MapBounds.east,
  //         }
  //       ) * 1000;
  //     const radius = Math.min(distance_lat, distance_lon, 50000);
  //     const center = {
  //       latitude: (MapBounds.north + MapBounds.south) / 2,
  //       longitude: (MapBounds.west + MapBounds.east) / 2,
  //     };
  //     return { center: center, radius: radius };
  //   };

  //   const _googleTextSearch = (inputs) => {
  //     if (inputs === null) return;
  //     const searchText = inputs[0].value;
  //     setSavedTextSearch(searchText);
  //     //this.setState({ visiblePrompt: false, placeProvider: "google" });
  //     // if (this.state.searchText.length < 3) {
  //     //   Alert.alert("Search text must be at least 3 characters");
  //     //   return;
  //     // }
  //     dispatch(slice1Actions.SetLoadingGuide({ value: true }));
  //     //location, radius, type, query, callback;

  //     const distance_lat =
  //       Places.calculateDistance(
  //         {
  //           latitude: MapBounds.south,
  //           longitude: MapBounds.west,
  //         },
  //         {
  //           latitude: MapBounds.north,
  //           longitude: MapBounds.east,
  //         }
  //       ) * 1000;
  //     const distance_lon =
  //       Places.calculateDistance(
  //         {
  //           latitude: MapBounds.south,
  //           longitude: MapBounds.west,
  //         },
  //         {
  //           latitude: MapBounds.north,
  //           longitude: MapBounds.east,
  //         }
  //       ) * 1000;
  //     const radius = Math.min(distance_lat, distance_lon, 50000);
  //     //console.log("googleTextSearch", distance_lat, distance_lon, radius);
  //     //location, radius, type, query, callback
  //     _GooglePlaces.searchPlaces(
  //       `${((MapBounds.north + MapBounds.south) / 2).toString()},${((MapBounds.east + MapBounds.west) / 2).toString()}`,
  //       radius,
  //       null,
  //       searchText,
  //       searchGooglePlaceCallback
  //     );
  //   };

  //   const searchGooglePlaceCallback = (google_places) => {
  //     if (google_places == null || google_places.length == 0) {
  //       //console.log("searchGooglePlaceCallback google_places", google_places);
  //       dispatch(slice1Actions.SetLoadingGuide({ value: false }));
  //       return;
  //     }
  //     //console.log("searchGooglePlaceCallback google_places.length", google_places.length);
  //     //console.log("otm_places", otm_places);
  //     _GooglePlaces.getDetails(google_places, getGoogleDetailsCallback);
  //   };

  //   const getGoogleDetailsCallback = (places) => {
  //     //console.log("getGoogleDetailsCallback", places);
  //     dispatch(slice1Actions.setSearchResultsPlacesList({ value: places }));
  //     dispatch(slice1Actions.setGuide({ guide: null }));
  //     dispatch(slice1Actions.SetLoadingGuide({ value: false }));
  //   };

  //   const wikipediaTextSearch = () => {
  //     setPromptModalShow(true);
  //     setPromptModalTitle("Search");
  //     setPromptModalMessage("Wikipedia text");
  //     setPromptModalButtons([
  //       { Title: "OK", Method: _wikipediaTextSearch },
  //       { Title: "CANCEL", Method: cancelSearch },
  //     ]);
  //     setPromptModalInputs([{ name: "Search Text", value: SavedTextSearch }]);
  //   };

  //   const _wikipediaTextSearch = (inputs) => {
  //     if (inputs === null) return;
  //     const searchText = inputs[0].value;
  //     setSavedTextSearch(searchText);
  //     dispatch(slice1Actions.SetLoadingGuide({ value: true }));

  //     console.log("PlacesHeader MapBounds", MapBounds);

  //     let deltaLat = Math.abs(Math.min(0.25, MapBounds.north - MapBounds.south)) / 2;
  //     let deltaLon = Math.abs(Math.min(0.18, MapBounds.east - MapBounds.west)) / 2;

  //     let centerLat = (MapBounds.north + MapBounds.south) / 2;
  //     let centerLon = (MapBounds.west + MapBounds.east) / 2;

  //     _Wikipedia.searchPlaces(
  //       `${(centerLat + deltaLat).toString()}|${(centerLon - deltaLon).toString()}|${(
  //         centerLat - deltaLat
  //       ).toString()}|${(centerLon + deltaLon).toString()}`,
  //       200,
  //       searchWikipediaCallback
  //     );
  //   };

  //   let _wikipedia_geosearch_results = [];
  //   let _wikepedia_pages = [];
  //   let _wiki_calls = 1;
  //   let _wiki_callbacks = 0;

  //   const searchWikipediaCallback = (wikipedia_places) => {
  //     //console.log("searchWikipediaCallback", wikipedia_places);
  //     if (wikipedia_places === null) {
  //       dispatch(slice1Actions.SetLoadingGuide({ value: false }));
  //       myAlert("Try smaller search area");
  //       return;
  //     }
  //     if (typeof wikipedia_places["query"] == "undefined") {
  //       dispatch(slice1Actions.SetLoadingGuide({ value: false }));
  //       //console.log("searchWikipediaCallback ", wikipedia_places);
  //       const info =
  //         typeof wikipedia_places["error"]["info"] == "undefined"
  //           ? "Try smaller search area"
  //           : wikipedia_places["error"]["info"];
  //       dispatch(slice1Actions.SetLoadingGuide({ value: false }));
  //       myAlert(info);
  //       return;
  //     }

  //     if (wikipedia_places == null || wikipedia_places.length == 0) {
  //       //console.log("searchWikipediaCallback wikipedia_places, null or zero");
  //       dispatch(slice1Actions.SetLoadingGuide({ value: false }));
  //       myAlert("No results try a smaller or bigger area");
  //       return;
  //     }
  //     if (typeof wikipedia_places["query"] == "undefined") {
  //       dispatch(slice1Actions.SetLoadingGuide({ value: false }));
  //       return;
  //     }
  //     if (typeof wikipedia_places["query"]["geosearch"] == "undefined") {
  //       dispatch(slice1Actions.SetLoadingGuide({ value: false }));
  //       return;
  //     }
  //     _wikepedia_pages = [];
  //     _wikipedia_geosearch_results = wikipedia_places["query"]["geosearch"];
  //     //console.log("_wikipedia_geosearch_results.length", _wikipedia_geosearch_results.length);
  //     if (_wikipedia_geosearch_results.length == 0) {
  //       dispatch(slice1Actions.SetLoadingGuide({ value: false }));
  //       myAlert("No results try a smaller area");
  //       return;
  //     }
  //     dispatch(slice1Actions.SetLoadingGuide({ value: false }));
  //     _wiki_calls = _wikipedia_geosearch_results.length / 50;
  //     _wiki_callbacks = 0;
  //     //for (let i = 0; i < _wiki_calls; i++) {
  //     const number = _wikipedia_geosearch_results.length >= 50 ? 50 : _wikipedia_geosearch_results.length;
  //     let batch = _wikipedia_geosearch_results.slice(0, number);
  //     //console.log("batch", _wikipedia_geosearch_results.length, batch.length);
  //     _Wikipedia.getDetails(batch, getWikipediaCallback);
  //     //}
  //   };

  //   const getWikipediaCallback = (places) => {
  //     const _places = _Wikipedia.parsePlaces(_wikipedia_geosearch_results, places, SavedTextSearch);
  //     _wikepedia_pages = _wikepedia_pages.concat(_places);
  //     _wiki_callbacks += 1;
  //     if (_wiki_callbacks >= _wiki_calls) {
  //       processWikepediaPages();
  //       return;
  //     }
  //     const number =
  //       _wikipedia_geosearch_results.length >= 50 * (_wiki_callbacks + 1)
  //         ? 50
  //         : _wikipedia_geosearch_results.length - 50 * _wiki_callbacks;
  //     let batch = _wikipedia_geosearch_results.slice(_wiki_callbacks * 50, _wiki_callbacks * 50 + number);

  //     _Wikipedia.getDetails(batch, getWikipediaCallback);
  //   };

  //   const processWikepediaPages = () => {
  //     dispatch(slice1Actions.setSearchResultsPlacesList({ value: _wikepedia_pages }));
  //     dispatch(slice1Actions.setGuide({ guide: null }));
  //     dispatch(slice1Actions.SetLoadingGuide({ value: false }));
  //   };

  const saveNewPlace = async () => {
    const city = addPlaceName.trim();
    const country = addPlaceCountry;
    if (!city || !country) return;
    setAddPlaceError("");
    setAddPlaceSaving(true);
    try {
      const query = encodeURIComponent(`${city}, ${country}`);
      const resp = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${query}&key=${process.env.REACT_APP_GOOGLE_MAPS_GEOCODING_KEY}`);
      const r = await resp.json();
      if (r.status === "OK" && r.results.length > 0) {
        const loc = r.results[0].geometry.location;
        if (loc.lat === 0 && loc.lng === 0) throw new Error("zero");
        const new_place = new Place(window.guide_obj.places);
        new_place.Name = `${city}, ${country}`;
        new_place.City = city;
        new_place.Country = country;
        new_place.Visited = addPlaceVisited;
        new_place.Lat = loc.lat;
        new_place.Longi = loc.lng;
        const findOrCreate = (name) => { const g = window.guide_obj.findGroupFromName(name); return g ? g.id : window.guide_obj.addGroup(name); };
        new_place.Group = [findOrCreate(city), findOrCreate(country)];
        window.guide_obj.places.addPlace(new_place);
        setAddPlaceShow(false);
        dispatch(slice1Actions.setGuide({ guide: null }));
        dispatch(slice1Actions.centerMapToPlaces({ value: [new_place] }));
      } else {
        setAddPlaceError(`Could not find "${city}, ${country}". Check spelling and try again.`);
      }
    } catch (_) {
      setAddPlaceError(`Could not find "${city}, ${country}". Check spelling and try again.`);
    } finally {
      setAddPlaceSaving(false);
    }
  };

  const sortedCountries = [...countries].sort();

  const AddPlacesBulk = () => {
    setAddPlacesBulkText("");
    setAddPlacesBulkGroup("");
    setAddPlacesBulkVisited(false);
    setAddPlacesBulkErrorNote("");
    setAddPlacesBulkProgress("");
    setAddPlacesBulkShow(true);
  };

  const saveBulkPlaces = async () => {
    const trimmed = addPlacesBulkText.trim();
    if (!trimmed) return;

    let entries = [];
    if (trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        entries = parsed.map((obj) => ({
          placeName: obj.name || null,
          city: (obj.city || "").trim(),
          country: (obj.country || "").trim(),
          raw: JSON.stringify(obj),
        }));
      } catch (_) {
        setAddPlacesBulkErrorNote("Invalid JSON. Check syntax and try again.");
        return;
      }
    } else {
      const lines = trimmed.split("\n").map(l => l.trim()).filter(l => l.length > 0);
      entries = lines.map((line) => {
        const parts = line.split(";").map(p => p.trim());
        if (parts.length >= 3) return { placeName: parts[0], city: parts[1], country: parts.slice(2).join(";").trim(), raw: line };
        if (parts.length === 2) return { placeName: null, city: parts[0], country: parts[1], raw: line };
        return { placeName: null, city: null, country: null, raw: line };
      });
    }

    if (entries.length === 0) return;
    setAddPlacesBulkProcessing(true);
    setAddPlacesBulkProgress(`0 / ${entries.length}`);
    setAddPlacesBulkErrorNote("");
    const failed = [];
    let addedCount = 0;
    for (let i = 0; i < entries.length; i++) {
      const { placeName, city, country, raw } = entries[i];
      setAddPlacesBulkProgress(`${i + 1} / ${entries.length}`);
      if (!city || !country) { failed.push(raw); continue; }
      if (!sortedCountries.includes(country)) { failed.push(raw); continue; }
      try {
        const query = encodeURIComponent(placeName ? `${placeName}, ${city}, ${country}` : `${city}, ${country}`);
        const resp = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${query}&key=${process.env.REACT_APP_GOOGLE_MAPS_GEOCODING_KEY}`);
        const data = await resp.json();
        const loc = data.status === "OK" && data.results.length > 0 ? data.results[0].geometry.location : null;
        if (loc && !(loc.lat === 0 && loc.lng === 0)) {
          const new_place = new Place(window.guide_obj.places);
          new_place.Name = placeName || city;
          new_place.City = city;
          new_place.Country = country;
          new_place.Visited = addPlacesBulkVisited;
          new_place.Lat = loc.lat;
          new_place.Longi = loc.lng;
          const findOrCreate = (name) => { const g = window.guide_obj.findGroupFromName(name); return g ? g.id : window.guide_obj.addGroup(name); };
          const groupNames = [...new Set([addPlacesBulkGroup.trim(), city, country].filter(n => n))];
          new_place.Group = groupNames.map(findOrCreate);
          window.guide_obj.places.addPlace(new_place);
          addedCount++;
        } else {
          failed.push(raw);
        }
      } catch (_) {
        failed.push(raw);
      }
    }
    setAddPlacesBulkProcessing(false);
    setAddPlacesBulkProgress("");
    if (addedCount > 0) dispatch(slice1Actions.setGuide({ guide: null, fitToBounds: true }));
    if (failed.length === 0) {
      setAddPlacesBulkShow(false);
      setAddPlacesBulkText("");
    } else {
      setAddPlacesBulkText(failed.join("\n"));
      setAddPlacesBulkErrorNote(`${failed.length} place${failed.length > 1 ? "s" : ""} could not be added (shown below). Check spelling or country name.`);
    }
  };

  const _haversineMeters = Common.haversineMeters;

  // One matching pass per photo: near an existing place (within
  // sensitivity) → candidate for that place (cover and/or notes); no
  // nearby place → candidate for a new place, clustered together with
  // other nearby-in-time-and-space unmatched photos via the same
  // proximity-clustering helper Flow 1 ("Create Journal from Photos")
  // uses, so this doesn't create one new place per unmatched photo.
  const _runPersonalizeMatch = (geoPhotos, sensitivityMeters) => {
    const allPlaces = window.guide_obj.places.placesList;
    const matchedIdxs = new Set();
    const matches = [];
    // Places with a real location but no photo within range at the current
    // sensitivity — surfaced in the review UI so the user can tell whether
    // to raise the distance, or that a place is simply missing a photo.
    const unmatchedPlaces = [];
    allPlaces.forEach((place) => {
      if (!place.Lat || !place.Longi) return;
      const candidates = geoPhotos
        .map((photo, idx) => ({
          photo,
          idx,
          dist: _haversineMeters(place.Lat, place.Longi, photo.exif_data.latitude, photo.exif_data.longitude),
        }))
        .filter((c) => c.dist <= sensitivityMeters)
        .sort((a, b) => a.dist - b.dist)
        .slice(0, 5);
      if (candidates.length > 0) {
        candidates.forEach((c) => matchedIdxs.add(c.idx));
        matches.push({ place, candidates });
      } else {
        unmatchedPlaces.push(place);
      }
    });
    const unmatched = geoPhotos.filter((_, idx) => !matchedIdxs.has(idx));
    const WEB_SUBSTITUTE_METERS = Math.max(1000, sensitivityMeters);
    const webExtras = unmatchedPlaces
      .filter((place) => isWebPhoto(place.Photo_m || place.Photo))
      .map((place) => ({
        place,
        candidates: geoPhotos
          .map((photo, idx) => ({ photo, idx, dist: _haversineMeters(place.Lat, place.Longi, photo.exif_data.latitude, photo.exif_data.longitude) }))
          .filter((c) => c.dist <= WEB_SUBSTITUTE_METERS)
          .sort((a, b) => a.dist - b.dist)
          .slice(0, 5),
      }));
    setPersonalizeWebExtras(webExtras);
    return { matches, unmatched, unmatchedPlaces, webExtras };
  };

  const _clusterUnmatchedPhotos = (unmatched, sensitivityMeters, maxSpanMeters) => {
    const sorted = [...unmatched].sort((a, b) => {
      const at = a.exif_data?.date ? Date.parse(a.exif_data.date) : 0;
      const bt = b.exif_data?.date ? Date.parse(b.exif_data.date) : 0;
      return at - bt;
    });
    return Common.clusterPhotosByProximity(sorted, sensitivityMeters, maxSpanMeters);
  };

  // Extra floor under "new place" candidacy — independent of, and usually
  // much larger than, the sensitivity distance above. A photo only survives
  // as a new-place candidate if it's farther than minDistanceMeters from
  // EVERY existing place in the guide, not just the ones near enough to
  // already be excluded by sensitivity. A no-op when minDistanceMeters is 0.
  const _filterFarFromExistingPlaces = (photos, minDistanceMeters) => {
    if (!minDistanceMeters || minDistanceMeters <= 0) return photos;
    const allPlaces = window.guide_obj.places.placesList.filter((p) => p.Lat && p.Longi);
    if (allPlaces.length === 0) return photos;
    return photos.filter((photo) =>
      allPlaces.every(
        (place) => _haversineMeters(place.Lat, place.Longi, photo.exif_data.latitude, photo.exif_data.longitude) >= minDistanceMeters
      )
    );
  };

  const _personalizeGuideCallback = (photos) => {
    const geoPhotos = photos.filter(
      (p) => p.exif_data?.latitude != null && p.exif_data?.longitude != null
    );
    if (geoPhotos.length === 0) {
      myAlert("None of the selected photos have GPS location data.");
      return;
    }
    personalizeGeoPhotosRef.current = geoPhotos;
    const { matches, unmatched, unmatchedPlaces, webExtras } = _runPersonalizeMatch(geoPhotos, personalizeSensitivity);
    const farUnmatched = _filterFarFromExistingPlaces(unmatched, personalizeMinNewPlaceDistance);
    const newClusters = _clusterUnmatchedPhotos(farUnmatched, personalizeSensitivity, personalizeMaxSpan);
    // Still opens when only web-photo places can be shown (their substitutes are farther away).
    if (matches.length === 0 && newClusters.length === 0 && !webExtras.some((w) => w.candidates.length > 0)) {
      myAlert("No matches or new places found. Try increasing the distance.");
      return;
    }
    setPersonalizeMatchedItems(matches);
    setPersonalizeNewClusters(newClusters);
    setPersonalizeUnmatchedPlaces(unmatchedPlaces);
    setPersonalizeReviewShow(true);
  };

  const _onPersonalizeSensitivityChange = (value) => {
    setPersonalizeSensitivity(value);
    const { matches, unmatched, unmatchedPlaces } = _runPersonalizeMatch(personalizeGeoPhotosRef.current, value);
    setPersonalizeMatchedItems(matches);
    const farUnmatched = _filterFarFromExistingPlaces(unmatched, personalizeMinNewPlaceDistance);
    setPersonalizeNewClusters(_clusterUnmatchedPhotos(farUnmatched, value, personalizeMaxSpan));
    setPersonalizeUnmatchedPlaces(unmatchedPlaces);
  };

  const _onPersonalizeMaxSpanChange = (value) => {
    setPersonalizeMaxSpan(value);
    const { matches, unmatched, unmatchedPlaces } = _runPersonalizeMatch(personalizeGeoPhotosRef.current, personalizeSensitivity);
    setPersonalizeMatchedItems(matches);
    const farUnmatched = _filterFarFromExistingPlaces(unmatched, personalizeMinNewPlaceDistance);
    setPersonalizeNewClusters(_clusterUnmatchedPhotos(farUnmatched, personalizeSensitivity, value));
    setPersonalizeUnmatchedPlaces(unmatchedPlaces);
  };

  const _onPersonalizeMinNewPlaceDistanceChange = (value) => {
    setPersonalizeMinNewPlaceDistance(value);
    const { matches, unmatched, unmatchedPlaces } = _runPersonalizeMatch(personalizeGeoPhotosRef.current, personalizeSensitivity);
    setPersonalizeMatchedItems(matches);
    const farUnmatched = _filterFarFromExistingPlaces(unmatched, value);
    setPersonalizeNewClusters(_clusterUnmatchedPhotos(farUnmatched, personalizeSensitivity, personalizeMaxSpan));
    setPersonalizeUnmatchedPlaces(unmatchedPlaces);
  };

  const _buildEmbedHtml = (photos) => {
    return photos
      .map((photo) => {
        const rawUrl = photo.url_m || photo.url || "";
        const idAttr = rawUrl ? ` data-photo-id="${rawUrl}"` : "";
        const resolved = rawUrl ? Common.getImageURL(rawUrl, null, false) : "";
        const src = (resolved && resolved.startsWith("http")) ? resolved : (photo.thumb || photo.url || rawUrl);
        return `<img src="${src}" alt=""${idAttr} style="max-width:100%;height:auto;margin:4px 0;">`;
      })
      .join("\n");
  };

  // Apply order matters: new places are created first (so the whole pass
  // is internally consistent), then ★ cover selections, then ☑ notes
  // embeds — computed against each place's now-updated Photo/Photo_m, so a
  // photo just set as a place's cover is naturally excluded from also
  // being redundantly embedded in that same place's Notes (same dedup
  // check Add Photos to Places already had).
  const _applyPersonalizeGuide = ({ newPlaces, matches, guideCoverPhoto, asSearchResults = false, deletePlaceIds = [] }) => {
    // Web-photo places the person never went to (2026-10-28): the place goes,
    // and so do its events (an event shared with other places keeps those).
    deletePlaceIds.forEach((id) => {
      window.guide_obj.places.deletePlaceById(id);
      removePlaceFromEvents(window.guide_obj.schedule, id);
    });
    if (guideCoverPhoto) {
      const guideCoverUrl = guideCoverPhoto.url_m || guideCoverPhoto.url || "";
      window.guide_obj.attributes.image = guideCoverUrl;
      window.guide_obj.attributes.image_m = guideCoverUrl;
    }

    let createdCount = 0;
    const searchPlaces = [];
    const usedNames = new Set(window.guide_obj.places.placesList.map((p) => (p.Name || "").toLowerCase()));
    newPlaces.forEach(({ coverPhoto, extraPhotos, placeName }) => {
      // placeName, when set, only overrides the resulting place's name —
      // never the photo's own filename/file, which stays untouched.
      let _name = placeName || coverPhoto.name || "New Place";
      if (!placeName && _name.toUpperCase().includes(".JPG")) _name = _name.slice(0, -4);
      _name = Common.uniquePlaceName(_name, usedNames);
      const new_place = new Place(window.guide_obj.places);
      new_place.Name = _name;
      new_place.Lat = coverPhoto.exif_data.latitude;
      new_place.Longi = coverPhoto.exif_data.longitude;
      new_place.Photo_m = coverPhoto.url_m || coverPhoto.url || "";
      new_place.Photo = coverPhoto.url || coverPhoto.url_m || "";
      new_place.poi_img = "208";
      if (extraPhotos.length > 0) new_place.Info = _buildEmbedHtml(extraPhotos);
      // "Show new places as search results" (2026-10-19, replaces the app's
      // old Search Places from Photos): onto the map to add one by one.
      if (asSearchResults) {
        new_place.Source = Place_Source.PHOTO_SEARCH;
        new_place.PType = "Icon";
        searchPlaces.push(new_place);
        return;
      }
      window.guide_obj.places.addPlace(new_place);
      createdCount++;
    });
    if (searchPlaces.length > 0) dispatch(slice1Actions.setSearchResultsPlacesList({ value: searchPlaces }));

    let coverCount = 0;
    matches.forEach(({ place, coverPhoto }) => {
      if (!coverPhoto) return;
      const photoSmall = coverPhoto.url || coverPhoto.url_m || "";
      const photoLarge = coverPhoto.url_m || coverPhoto.url || "";
      window.guide_obj.places.updatePhoto({ Photo: photoSmall, Photo_m: photoLarge }, place.ID);
      coverCount++;
    });

    let notesCount = 0;
    matches.forEach(({ place, notesCandidates }) => {
      if (!notesCandidates || notesCandidates.length === 0) return;
      const infoHtml = place.Info || "";
      const toEmbed = notesCandidates
        .map((c) => c.photo)
        .filter((photo) => {
          const rawUrl = photo.url_m || photo.url || "";
          if (rawUrl && (place.Photo === rawUrl || place.Photo_m === rawUrl)) return false;
          if (rawUrl && infoHtml.includes(rawUrl)) return false;
          if (rawUrl && infoHtml.includes(`data-photo-id="${rawUrl}"`)) return false;
          return true;
        });
      if (toEmbed.length === 0) return;
      place.Info = (place.Info || "") + "\n" + _buildEmbedHtml(toEmbed);
      notesCount++;
    });

    dispatch(slice1Actions.setGuide({ guide: null, fitToBounds: false }));
    setPersonalizeReviewShow(false);
    const parts = [];
    if (createdCount > 0) parts.push(`${createdCount} new place${createdCount !== 1 ? "s" : ""} created`);
    if (coverCount > 0) parts.push(`${coverCount} cover photo${coverCount !== 1 ? "s" : ""} updated`);
    if (notesCount > 0) parts.push(`${notesCount} place${notesCount !== 1 ? "s" : ""} got new notes photos`);
    if (guideCoverPhoto) parts.push("guide cover photo updated");
    if (searchPlaces.length > 0) parts.push(`${searchPlaces.length} new place${searchPlaces.length !== 1 ? "s" : ""} shown on the map as search results — tap + on any to add it`);
    if (parts.length > 0) myAlert(parts.join(", ") + ".");
  };

  const _startPersonalizeGuide = () => {
    setPersonalizeShow(false);
    setTimeout(() => {
      if (personalizeSource === "gdrive") setGoogleDrivePickerShow(true);
      else setImageDialogShow(true);
    }, 300);
  };

  const removeInfoFromPlaces = () => {
    _place_operation = "Remove Info";
    selectPlaces();
  };

  const _showEditMenu = (menuList, heading, backFn = null) => {
    setMenuModalBackFn(backFn ? () => backFn : null);
    setMenuModalList(menuList);
    setMenuModalHeading(heading);
    setMenuModalImage(<FontAwesomeIcon icon={solid("pencil")} />);
    setMenuModalPlace(null);
    setMenuModalInfo(
      "This applies to all filtered places i.e. all places shown in the places list. Apply or remove filters to select desired places."
    );
    setMenuModalShow(true);
  };

  const EditContentMenu = () => {
    const buildMenuList = (advancedMode) => [
        {
          label: "Auto Add Info to Notes",
          method: FindPlaceInfo,
          icon: null,
          visible: advancedMode,
          info: "Search Wikipedia for a short description and link for each selected place and add it to the place info field.",
        },
        {
          label: "Find Opening Times",
          method: FindOpeningTimes,
          icon: null,
          visible: advancedMode,
          info: "Look up regular weekly opening hours from Google for each selected place and fill in the Opening Hours field.",
          videoKey: "opening-hours-and-schedule-conflicts",
        },
        {
          label: "Remove Notes from Places",
          method: removeInfoFromPlaces,
          icon: null,
          visible: advancedMode,
          info: "Clear the Info (notes/HTML) field from all places. This cannot be undone.",
        },
        { label: "**SEPARATOR***", method: null, icon: null, visible: true },
        {
          label: "Refresh Photos",
          method: RefreshPhotosPrompt,
          icon: null,
          visible: true,
          info: "Repair broken/missing photos only, or force a fresh search for every selected place regardless of its current photo — you'll be asked which. Uses only images with no usage conditions (public domain / CC0) from Wikimedia Commons and Flickr, plus the featured-places list, so nothing needs a credit.",
          videoKey: "refresh-photos",
        },
        {
          label: "Refresh Photos (Paid)",
          method: UpdateAllPhotosPaid,
          icon: null,
          // Hidden again 2026-09-05 — HasData's free tier is only 200
          // searches/mo, real risk of exhausting it via a bulk run.
          // Re-enable once a paid tier/quota decision is made. The feature
          // itself (queue processor, cost-confirm dialog) is intact, just
          // not reachable from the menu.
          visible: false,
          info: "Same as Refresh Photos, but also tries a paid source first for a real business-listing photo (better quality than the free sources) — uses one paid search per selected place, so this has a real, ongoing cost. Confirms the place count before running.",
        },
        {
          label: "Clear Photos",
          method: ClearPhotos,
          icon: null,
          visible: advancedMode,
          info: "Remove the photo from the selected places and show the default image instead. Cleared places are skipped by later Refresh Photos runs. Use it to undo clearly wrong photos.",
        },
        {
          label: "Personalize Guide with Photos",
          method: () => setPersonalizeShow(true),
          icon: null,
          visible: true,
          info: "Match your trip photos to nearby places by GPS proximity — set cover photos, add extra photos to notes, turn photos from new locations into new places (optionally identified by name, one at a time or all at once), and require new places to be a minimum distance from existing ones to surface excursions, all in one pass.",
          videoKey: "personalize-guide-with-photos",
        },
        {
          label: "Identify Places",
          method: () => setIdentifyPlacesShow(true),
          icon: null,
          visible: true,
          info: "Name the places already in your guide from where they are — e.g. ones still called after a photo file: nearby landmarks, the city, or a spot picked on the map, with each place's photos (web photos labelled). Renames their events too; asks before changing the itinerary.",
        },
        // {
        //   label: "Remove Duplicate Photos",
        //   method: removeDuplicatePhotos,
        //   icon: null,
        //   visible: true,
        //   info: "Remove duplicate images from place notes. Detects duplicates by matching image URL or photo source ID.",
        // },
        { label: "**SEPARATOR***", method: null, icon: null, visible: true },
        {
          label: "Set Place Link",
          method: _setPathlink,
          icon: null,
          visible: advancedMode,
          info: "Set the place link for the place in order to enable play video, audio etc.",
        },
    ];
    let menuList = buildMenuList(advancedMode);
    if (!advancedMode) {
      const hiddenCount = buildMenuList(true).filter((i) => i.visible).length - menuList.filter((i) => i.visible).length;
      if (hiddenCount > 0) {
        menuList = [
          ...menuList,
          {
            label: `${hiddenCount} more option${hiddenCount === 1 ? "" : "s"} in Advanced Mode`,
            method: () => {
              window.dispatchEvent(new CustomEvent("toggleAdvancedMode"));
              setTimeout(() => { if (editContentMenuRef.current) editContentMenuRef.current(); }, 350);
            },
            icon: null,
            visible: true,
            highlight: true,
            info: "Turn on Advanced Mode in Settings to see these options.",
          },
        ];
      }
    }
    _showEditMenu(menuList, "Content", EditMenu);
  };

  // Not Advanced-Mode-gated, unlike EditContentMenu/EditMenu's sibling
  // submenus — every single item here was classified "advanced" (bulk
  // group/category/attribute edits), so gating would have hidden the ENTIRE
  // submenu behind a single "N more options in Advanced Mode" row with no
  // basic subset left to show. That's not what Advanced Mode gating is for —
  // it's meant to trim a menu down to a smaller, still-useful basic core, not
  // replace a whole submenu with a mode-toggle prompt. Found 2026-10-04.
  const EditAttributesMenu = () => {
    const menuList = [
        {
          label: "Add to Groups",
          method: AddGroups,
          icon: null,
          visible: true,
          info: "Add the places in the places list to one or more groups. Note that the places in the places list can be filtered.",
        },
        {
          label: "Remove from Groups",
          method: RemoveGroups,
          icon: null,
          visible: true,
          info: "Remove the places in the places list from selected groups. Note that the places in the places list can be filtered.",
        },
        {
          label: "Set to Groups",
          method: ReplaceGroups,
          icon: null,
          visible: true,
          info: "Remove the places in the places list from all groups and then add them only to the selected groups. Note that the places in the places list can be filtered.",
        },
        { label: "**SEPARATOR***", method: null, icon: null, visible: true },
        {
          label: "Add to Categories",
          method: AddCategories,
          icon: null,
          visible: true,
          info: "Add the places in the places list to the selected categories. Note that the places in the places list can be filtered.",
        },
        {
          label: "Remove from Categories",
          method: RemoveCategories,
          icon: null,
          visible: true,
          info: "Remove the places in the places list from selected categories. Note that the places in the places list can be filtered.",
        },
        {
          label: "Set to Categories",
          method: ReplaceCategories,
          icon: null,
          visible: true,
          info: "Remove the places in the places list from all categories and add them only to the selected categories. Note that the places in the places list can be filtered.",
        },
        {
          label: "Fix Categories",
          method: FixCategories,
          icon: null,
          visible: true,
          info: "Assign a category to any place in the places list that doesn't have one yet, using the featured-places manifest or a best-effort match against the place's name. Already-categorized places are left untouched. Note that the places in the places list can be filtered.",
        },
        { label: "**SEPARATOR***", method: null, icon: null, visible: true },
        {
          label: "Mark Places Favorites",
          method: EditFavorite,
          icon: null,
          visible: true,
          info: "Set the 'Favorite' attribute for the places in the places list, i.e. Favorite or not Favorite",
          videoKey: "place-attributes",
        },
        {
          label: "Mark Places Visited",
          method: EditVisited,
          icon: null,
          visible: true,
          info: "Set the 'Visited' attribute for the places in the places list, i.e. Visited or not Visited",
          videoKey: "place-attributes",
        },
        {
          label: "Mark Places as 'Must See'",
          method: EditMustSee,
          icon: null,
          visible: true,
          info: "Set the 'Must See' attribute for the places in the places list, i.e. Must See or not Must See",
          videoKey: "place-attributes",
        },
        {
          label: "Mark Places as 'Bucket List'",
          method: EditBucketList,
          icon: null,
          visible: true,
          info: "Set the 'Bucket List' attribute for the places in the places list, i.e. Bucket List or not Bucket List",
          videoKey: "place-attributes",
        },
        {
          label: "Show/Hide Places",
          method: EditHidden,
          icon: null,
          visible: true,
          info: "Set the 'Hidden' attribute for the places in the places list, i.e. Hidden or not Hidden. Hidden places are not shown on map or places list.",
        },
        { label: "**SEPARATOR***", method: null, icon: null, visible: true },
        {
          label: "Set City",
          method: SetCity,
          icon: null,
          visible: true,
          info: "Set the city for the selected places.",
        },
        {
          label: "Set Country",
          method: SetCountry,
          icon: null,
          visible: true,
          info: "Set the country for the selected places.",
        },
        {
          label: "Fill Missing City & Country",
          method: AutoSetPlaceCountry,
          icon: null,
          visible: true,
          info: "Fill in a missing City and/or Country for the selected places, using nearby places in the guide first and a location lookup only for the rest. Existing values are never changed — needed for Wanderlist's country/continent statistics.",
        },
    ];
    _showEditMenu(menuList, "Attributes", EditMenu);
  };

  // "Delete Places" stays Advanced-Mode-gated even though it's the only
  // gated item in this menu — unlike EditAttributesMenu's fix above (15
  // same-character bulk-edit items with no standout destructive one to
  // anchor a gate on), Delete is the one unambiguous case: irrecoverable,
  // consistent with every other Delete action in the app staying gated
  // (PlaceModal's "Delete {placeType}", EventModal's "Delete Event"). A lone
  // gate is usually a sign to ungate the whole menu, but not when the lone
  // item is this clearly destructive. Found 2026-10-04.
  const EditMenu = () => {
    const buildMenuList = (advancedMode) => [
        // {
        //   label: "Add Place",
        //   method: () => setEditPlaceShow(true),
        //   icon: null,
        //   visible: true,
        //   info: "Add a new place by entering data ino a dialog.",
        // },
        {
          label: "Add Places",
          method: AddPlacesBulk,
          icon: null,
          visible: true,
          info: "Add multiple places at once. Type or paste one place per line in the format: City, Country.",
          videoKey: "add-places",
        },
        { label: "**SEPARATOR***", method: null, icon: null, visible: true },
        {
          label: "Content ›",
          method: EditContentMenu,
          icon: null,
          visible: true,
          info: "Edit place info text, photos, and links.",
        },
        {
          label: "Attributes ›",
          method: EditAttributesMenu,
          icon: null,
          visible: true,
          info: "Edit groups, categories, flags (Favorites, Visited, Must See, Hidden), city, and country.",
        },
        {
          label: "AI Search",
          method: () => setAISearchShow(true),
          icon: null,
          visible: true,
          info: "Find places with AI: describe what you want (e.g. romantic restaurants with a view) or pick a sample query, then add the results to your guide.",
          videoKey: "ai-search",
        },
        {
          label: "Search from Map",
          method: goToMapSearch,
          icon: null,
          visible: true,
          info: "Open the Map screen's Search menu — most search options (nearby, category, Wikipedia, Flickr, trails) are location-based and need the map.",
        },
        {
          label: "Build Trip from Places",
          method: BuildScheduleFromPlaces,
          icon: null,
          visible: (window.guide_obj?.places?.placesList?.length || 0) > 0,
          info: "Pick which places to include (all are pre-selected), choose a start date, and build into a new schedule or an existing one — ordered by travel distance, fit to each day's hours, with an itinerary generated automatically.",
          videoKey: "build-trip-from-places",
        },
        { label: "**SEPARATOR***", method: null, icon: null, visible: true },
        {
          label: "Remove Unused Places",
          method: RemoveUnusedPlaces,
          icon: null,
          visible: true,
          info: "Remove places that aren't used in any schedule. Run this any time — it never happens automatically.",
        },
        {
          label: "Delete Places",
          method: DeletePlaces,
          icon: null,
          visible: advancedMode,
          info: "Select which places should be deleted. Note: once deleted they cannot be recovered.",
        },
    ];
    let menuList = buildMenuList(advancedMode);
    if (!advancedMode) {
      const hiddenCount = buildMenuList(true).filter((i) => i.visible).length - menuList.filter((i) => i.visible).length;
      if (hiddenCount > 0) {
        menuList = [
          ...menuList,
          {
            label: `${hiddenCount} more option${hiddenCount === 1 ? "" : "s"} in Advanced Mode`,
            method: () => {
              window.dispatchEvent(new CustomEvent("toggleAdvancedMode"));
              setTimeout(() => { if (placesMenuRef.current) placesMenuRef.current(); }, 350);
            },
            icon: null,
            visible: true,
            highlight: true,
            info: "Turn on Advanced Mode in Settings to see these options.",
          },
        ];
      }
    }
    _showEditMenu(menuList, "Places Menu");
  };
  let AddReplaceMode = "Add";

  const AddCategories = (category) => {
    AddReplaceMode = "Add";
    _place_operation = "Categories";
    selectPlaces();
  };

  const RemoveCategories = (category) => {
    AddReplaceMode = "Remove";
    _place_operation = "Categories";
    selectPlaces();
  };

  const ReplaceCategories = (category) => {
    AddReplaceMode = "Replace";
    _place_operation = "Categories";
    selectPlaces();
  };

  const selectCategories = () => {
    let _ChooseCategoriesList = window.guide_obj.getCategoriesToDisplay();
    dispatch(slice1Actions.setMultiselectCallback({ value: SelectCategoryCallBack }));
    dispatch(slice1Actions.setMultiselectItems({ value: _ChooseCategoriesList }));
    dispatch(slice1Actions.setMultiselectSelectedItems({ value: [] }));
    dispatch(slice1Actions.setMultiselectMode({ value: "uni" }));
    dispatch(slice1Actions.setMultiselectSearchText({ value: "" }));
    dispatch(slice1Actions.setMultiselectTitle({ value: "Select Category" }));
    dispatch(slice1Actions.setShowMultiselect({ value: true }));
  };

  const SelectCategoryCallBack = (categories) => {
    _selected_places_for_country.forEach((placeItem) => {
      let place = window.guide_obj.places.getPlace(placeItem.value.ID);
      if (place != null) {
        if (AddReplaceMode === "Replace") place.Category = [];
        categories.forEach((category) => {
          if (AddReplaceMode === "Add" || AddReplaceMode === "Replace") {
            const index = place.Category.indexOf(category.id);
            if (index === -1) place.Category.push(category.id);
          } else if (AddReplaceMode === "Remove") {
            const index = place.Category.indexOf(category.id);
            if (index !== -1) place.Category.splice(index, 1);
          }
        });
      }
    });
    dispatch(slice1Actions.setGuide({ guide: null }));
  };

  const SelectFirstPlaceCallBack = (places) => {
    let place_id = null;
    if (places.length > 0) place_id = places[0].value.ID;
    createEventNew(place_id);
  };

  const AddGroups = (category) => {
    AddReplaceMode = "Add";
    _place_operation = "Groups";
    selectPlaces();
  };

  const RemoveGroups = (category) => {
    AddReplaceMode = "Remove";
    _place_operation = "Groups";
    selectPlaces();
  };

  const ReplaceGroups = (category) => {
    AddReplaceMode = "Replace";
    _place_operation = "Groups";
    selectPlaces();
  };

  const selectGroups = () => {
    let _ChooseCategoriesList = window.guide_obj.getGroupsToDisplay();
    dispatch(slice1Actions.setMultiselectCallback({ value: SelectGroupsCallBack }));
    dispatch(slice1Actions.setMultiselectItems({ value: _ChooseCategoriesList }));
    dispatch(slice1Actions.setMultiselectSelectedItems({ value: [] }));
    dispatch(slice1Actions.setMultiselectMode({ value: "multi" }));
    dispatch(slice1Actions.setMultiselectSearchText({ value: "" }));
    dispatch(slice1Actions.setMultiselectTitle({ value: "Select Groups" }));
    dispatch(slice1Actions.setShowMultiselect({ value: true }));
  };

  const SelectGroupsCallBack = (groups) => {
    _selected_places_for_country.forEach((placeItem) => {
      let place = window.guide_obj.places.getPlace(placeItem.value.ID);
      if (place != null) {
        if (AddReplaceMode === "Replace") place.Group = [];
        groups.forEach((group) => {
          if (AddReplaceMode === "Add" || AddReplaceMode === "Replace") {
            const index = place.Group.indexOf(group.id);
            if (index === -1) place.Group.push(group.id);
          } else if (AddReplaceMode === "Remove") {
            const index = place.Group.indexOf(group.id);
            if (index !== -1) place.Group.splice(index, 1);
          }
        });
      }
    });
    setTimeout(() => UpdateDisplay(), 500);
    dispatch(slice1Actions.setGuide({ guide: null }));
  };

  // Build My Trip with "This guide's places" (2026-10-17) — the old place
  // picker + Build Schedule dialog are replaced by it: per-destination dates,
  // the review list with photos, Overwrite. Opened by MobileBottomNavBar.js.
  // placeIds: a list of place IDs, or anything else (a menu calls this with
  // its own argument) = all places.
  const BuildScheduleFromPlaces = (placeIds = null) => {
    window.dispatchEvent(new CustomEvent("andiamo-build-my-trip-this-guide", { detail: { placeIds: Array.isArray(placeIds) && placeIds.length > 0 ? placeIds : null } }));
  };
  // With placeIds (the import hand-off): skip the picker and build from
  // exactly those places. Without: same as the menu item.
  buildFromPlacesRef.current = (placeIds) => {
    // Build Trip from Image or Text's new places: Build My Trip, limited to them.
    BuildScheduleFromPlaces(placeIds && placeIds.length > 0 ? placeIds : null);
  };

  // Replaces Build Trip from Places' old "Remove places that don't fit
  // schedule from guide" checkbox — that auto-pruned right after a build,
  // which was destructive if the user wanted to re-run the build with
  // different options (the just-deleted places could never be reconsidered).
  // This is the same cleanup, but explicit and user-invoked at any time,
  // scanning every schedule rather than just the one just built. Found/
  // added 2026-09-14.
  const RemoveUnusedPlaces = () => {
    const referencedIds = new Set();
    (window.guide_obj?.schedule?.events || []).forEach((event) => {
      (event.Places || "").split(",").map((id) => id.trim()).filter(Boolean).forEach((id) => referencedIds.add(id));
    });
    const unused = (window.guide_obj?.places?.placesList || []).filter((p) => !referencedIds.has(String(p.ID)));
    if (unused.length === 0) {
      myAlert("Every place in this guide is used in at least one schedule — nothing to remove.");
      return;
    }
    setAlertModalTitle("Remove Unused Places");
    setAlertModalMessage(
      `${unused.length} place${unused.length === 1 ? " isn't" : "s aren't"} used in any schedule. Remove ${unused.length === 1 ? "it" : "them"} from the guide? This can't be undone.`
    );
    setAlertModalButtons([
      {
        Title: "Remove",
        Method: () => {
          unused.forEach((p) => window.guide_obj.places.deletePlaceById(p.ID));
          dispatch(slice1Actions.setGuide({ guide: null, fitToBounds: true }));
          myAlert(`Removed ${unused.length} unused place${unused.length === 1 ? "" : "s"}.`);
        },
      },
      { Title: "Cancel", Method: null },
    ]);
    setAlertModalShow(true);
  };

  const DeletePlaces = () => {
    _place_operation = "Delete Places";
    selectPlaces();
  };

  const EditFavorite = () => {
    _place_operation = "Favorite";
    selectPlaces();
  };

  const EditVisited = () => {
    _place_operation = "Visited";
    selectPlaces();
  };

  const EditMustSee = () => {
    _place_operation = "MustSee";
    selectPlaces();
  };

  const EditBucketList = () => {
    _place_operation = "BucketList";
    selectPlaces();
  };

  const EditHidden = () => {
    _place_operation = "Hidden";
    selectPlaces();
  };

  const SetCity = () => {
    _place_operation = "SetCity";
    selectPlaces();
  };

  const SetCountry = () => {
    _place_operation = "SetCountry";
    selectPlaces();
  };

  const FindPlaceInfo = () => {
    _place_operation = "FindPlaceInfo";
    selectPlaces();
  };

  const FindOpeningTimes = () => {
    _place_operation = "FindOpeningTimes";
    selectPlaces();
  };

  const FixPhotos = () => {
    _place_operation = "FixPhotos";
    selectPlaces();
  };

  const UpdateAllPhotos = () => {
    _place_operation = "UpdateAllPhotos";
    selectPlaces();
  };

  // Single "Refresh Photos" menu entry replacing what used to be two
  // separate items (Fix Photos / Refresh Photos) — the distinction
  // between "only touches broken ones" and "touches everything" is
  // easier for a user to grasp as a plain question asked right when they
  // tap the menu item than as two similarly-named menu items they have to
  // already understand the difference between. Both existing entry
  // points (FixPhotos/UpdateAllPhotos) are unchanged; this only decides
  // which one runs. Found/added 2026-09-17.
  const RefreshPhotosPrompt = () => {
    setAlertModalTitle("Refresh Photos");
    setAlertModalMessage("Only refresh broken or missing photos, or refresh every selected place regardless of its current photo?");
    setAlertModalButtons([
      { Title: "Only Broken/Missing", Method: FixPhotos },
      { Title: "All Selected Photos", Method: UpdateAllPhotos },
      { Title: "Cancel", Method: null },
    ]);
    setAlertModalShow(true);
  };

  const ClearPhotos = () => {
    _place_operation = "ClearPhotos";
    selectPlaces();
  };

  const UpdateAllPhotosPaid = () => {
    _place_operation = "UpdateAllPhotosPaid";
    selectPlaces();
  };

  const AutoSetPlaceCountry = () => {
    _place_operation = "AutoSetCountry";
    selectPlaces();
  };

  const FixCategories = () => {
    _place_operation = "FixCategories";
    selectPlaces();
  };

  const _setPathlink = () => {
    createPlaceLinkMenu();

    // const places = window.guide_obj.places.getPlacesToDisplay(null, "PolyLine");

    // dispatch(slice1Actions.setMultiselectCallback({ value: setPathLinkCallBack }));
    // dispatch(slice1Actions.setMultiselectItems({ value: places }));
    // dispatch(slice1Actions.setMultiselectSelectedItems({ value: [] }));
    // dispatch(slice1Actions.setMultiselectMode({ value: "uni" }));
    // dispatch(slice1Actions.setMultiselectSearchText({ value: "" }));
    // dispatch(slice1Actions.setMultiselectTitle({ value: "Select Places" }));
    // dispatch(slice1Actions.setShowMultiselect({ value: true }));
  };

  const createPlaceLinkMenu = () => {
    let menuList = [
      {
        label: "Remove link",
        method: removeLink,
        icon: null,
        visible: true,
        checked: false,
        info: "Remove link from place",
      },
      {
        label: "Link to Path",
        method: setPathLink,
        icon: null,
        visible: true,
        checked: false,
        info: "Get link to a path which has time location info and associated video",
        videoKey: "link-a-place-to-a-path-and-video",
      },
      {
        label: "Link to Video",
        method: setVideoLink,
        icon: null,
        visible: true,
        checked: false,
        info: "Set a link to a video.",
      },
      {
        label: "Link to Audio",
        method: setAudioLink,
        icon: null,
        visible: true,
        checked: false,
        info: "Set a link to a audio.",
      },
      {
        label: "Link to Section (HTML)",
        method: selectSection,
        icon: null,
        visible: true,
        checked: false,
        info: "Set a link to a section which contains user customized HTML.",
      },
      {
        label: "Link to URL",
        method: setURLLink,
        icon: null,
        visible: true,
        checked: false,
        info: "Set a link to a URL i.e. web page e.g. https://google.com",
      },
    ];
    setMenuModalList(menuList);
    setMenuModalHeading("Place Link Menu");
    setMenuModalImage(<FontAwesomeIcon icon={solid("pencil")} />);
    setMenuModalPlace(null);
    setMenuModalInfo(
      "This applies to all filtered places i.e. all places shown in the places list. Apply or remove filters to select desired places."
    );
    setMenuModalShow(true);
    // this.setState({ MenuModalList: menuList, MenuModalHeading: "Place Link Menu", MenuModalShow: true });
  };

  const removeLink = () => {
    _place_operation = "SetLink";
    _link = { type: "none", value: "", offset: "0" };
    setTimeout(selectPlaces, 200);
  };

  const setPathLink = () => {
    const places = window.guide_obj.places.getPlacesToDisplay(null, "PolyLine");
    dispatch(slice1Actions.setMultiselectCallback({ value: setPathLinkCallBack }));
    dispatch(slice1Actions.setMultiselectItems({ value: places }));
    dispatch(slice1Actions.setMultiselectSelectedItems({ value: [] }));
    dispatch(slice1Actions.setMultiselectMode({ value: "uni" }));
    dispatch(slice1Actions.setMultiselectSearchText({ value: "" }));
    dispatch(slice1Actions.setMultiselectTitle({ value: "Select Places" }));
    dispatch(slice1Actions.setShowMultiselect({ value: true }));
  };

  const setPathLinkCallBack = (Places) => {
    if (Places.length < 1) return;
    const place = Places[0];
    _place_operation = "SetLink";
    _link = { type: "path", value: place.value, offset: "0" };
    setTimeout(selectPlaces, 200);
  };

  const setVideoLink = () => {
    setPromptModalShow(true);
    setPromptModalTitle("Video");
    setPromptModalMessage("Enter video URL");
    setPromptModalButtons([
      { Title: "OK", Method: setVideoLinkCallBack },
      { Title: "CANCEL", Method: null },
    ]);
    setPromptModalInputs([{ name: "Video URL", value: "" }]);
  };

  const setVideoLinkCallBack = (inputs) => {
    if (inputs.length < 1) return;
    _place_operation = "SetLink";
    _link = { type: "video", value: inputs[0].value, offset: "0" };
    setTimeout(selectPlaces, 200);
  };

  let _link = { type: "none", value: "", offset: "0" };

  const setAudioLink = () => {
    setPromptModalShow(true);
    setPromptModalTitle("Audio");
    setPromptModalMessage("Enter audio URL");
    setPromptModalButtons([
      { Title: "OK", Method: setAudioLinkCallBack },
      { Title: "CANCEL", Method: null },
    ]);
    setPromptModalInputs([{ name: "Audio URL", value: "" }]);
  };

  const setAudioLinkCallBack = (inputs) => {
    if (inputs.length < 1) return;
    _place_operation = "SetLink";
    _link = { type: "audio", value: inputs[0].value, offset: "0" };
    setTimeout(selectPlaces, 200);
  };

  const setURLLink = () => {
    setPromptModalShow(true);
    setPromptModalTitle("URL");
    setPromptModalMessage("Enter URL");
    setPromptModalButtons([
      { Title: "OK", Method: setURLLinkCallBack },
      { Title: "CANCEL", Method: null },
    ]);
    setPromptModalInputs([{ name: "URL", value: "" }]);
  };

  const setURLLinkCallBack = (inputs) => {
    if (inputs.length < 1) return;
    _place_operation = "SetLink";
    _link = { type: "url", value: inputs[0].value, offset: "0" };
    setTimeout(selectPlaces, 200);
  };

  const selectSection = () => {
    setSectionsDialogShow(true);
  };

  const selectSectionCallBack = (section) => {
    console.log("section", section.ID, section.Title);
    _place_operation = "SetLink";
    _link = { type: "section", value: section.ID, offset: "0" };
    setTimeout(selectPlaces, 200);
  };

  // const setPathLinkCallBack = (Places) => {
  //   if (Places.length < 1) return;
  //   const place = Places[0];
  //   _place_operation = "PathLink";
  //   pathLinkPlaceID = place.value;
  //   setTimeout(selectPlaces, 200);
  // };

  let AllPlacesSubList = [];
  const selectPlaces = () => {
    let selectedPlaces = [];
    let places = [];
    // When the Places view is grouped by Category (sortPlacesByCategory),
    // a place with more than one category is deliberately listed once per
    // matching section — correct for browsing, but this flattens every
    // section back into one list for operations like "Build Schedule from
    // Places"/Delete/etc., which must only ever touch each place once. A
    // multi-category place duplicated here meant "Build Schedule from
    // Places" scheduled it twice. Found 2026-08-31.
    const seenPlaceIds = new Set();
    filtered_places.forEach((section) => {
      section.data.forEach((place_m) => {
        let place = window.guide_obj.places.getPlace(place_m.place.ID);
        if (seenPlaceIds.has(place.ID)) return;
        seenPlaceIds.add(place.ID);
        places.push({
          label: place.Name,
          name: place.Name,
          value: place,
          // Photo, category · visit length, a line of notes for the actions
          // where you choose places (2026-10-17) — not the maintenance ones
          // (Fix photos, Find opening times…), where everything is selected.
          ...(PLACE_DETAIL_OPERATIONS.includes(_place_operation) ? { placeRow: placeRowFor(place, { categories: window.guide_obj.categories, user_info }) } : {}),
        });
      });
    });
    AllPlacesSubList = places;
    places.forEach((place) => {
      if (
        (_place_operation === "Favorite" && place.value.Favorite === true) ||
        (_place_operation === "Visited" && place.value.Visited === true) ||
        (_place_operation === "MustSee" && place.value.MustSee === true) ||
        (_place_operation === "BucketList" && place.value.BucketList === true) ||
        (_place_operation === "Hidden" && place.value.Hidden === true) ||
        _place_operation === "Delete Places" ||
        _place_operation === "Remove Info" ||
        // Build Trip from Places: every place pre-ticked except hotels — a
        // hotel is where the day starts (the schedule's "From <hotel>" row,
        // dayStartTravel.js), not a visit. Still listed, so it can be ticked.
        (_place_operation === "BuildSchedule" && !isHotel(place.value, hotelCategoryIds(window.guide_obj.categories))) ||
        _place_operation === "SetLink" ||
        _place_operation === "Groups" ||
        _place_operation === "Categories"
      ) {
        selectedPlaces.push({
          label: place.label,
          name: place.name,
          value: place.value,
        });
      }
    });
    //console.log("selectedPlaces, count", selectedPlaces, count);
    dispatch(slice1Actions.setMultiselectCallback({ value: selectPlacesCallBack }));
    dispatch(slice1Actions.setMultiselectItems({ value: places }));
    dispatch(slice1Actions.setMultiselectSelectedItems({ value: selectedPlaces }));
    dispatch(slice1Actions.setMultiselectMode({ value: "multi" }));
    dispatch(slice1Actions.setMultiselectSearchText({ value: "" }));
    dispatch(slice1Actions.setMultiselectTitle({ value: "Select Places" }));
    dispatch(slice1Actions.setShowMultiselect({ value: true }));
    // props.navigation.navigate("MultiSelectDialog", {
    //   selectionList: places,
    //   selectedItems: selectedPlaces,
    //   callBack: selectPlacesCallBack,
    //   title: "Places",
    //   selectText: "Select Places",
    // });
  };

  let _selected_places_for_country = [];

  let _place_operation = "";

  const selectPlacesCallBack = (Places) => {
    if (Places == null) return;
    let places_selected = Places;
    if (_place_operation === "Delete Places") {
      places_selected.forEach((place_id) => {
        window.guide_obj.places.deletePlaceById(place_id.value.ID);

        if (showHiddenPlace.length > 0) {
          let hidden_places = [];
          for (let i = 0; i < showHiddenPlace.length; i++) {
            if (showHiddenPlace[i].ID !== place_id.value.ID) {
              hidden_places.push(showHiddenPlace[i]);
              break;
            }
          }
          dispatch(slice1Actions.setShowHiddenPlace({ value: hidden_places }));
        }
      });
      setTimeout(() => UpdateDisplay(), 500);
      dispatch(slice1Actions.setGuide({ guide: null }));
      return;
    } else if (_place_operation === "Remove Info") {
      let count = 0;
      places_selected.forEach((place_id) => {
        const place = window.guide_obj.places.getPlace(place_id.value.ID);
        if (place && place.Info) {
          place.Info = "";
          count++;
        }
      });
      if (count > 0) {
        dispatch(slice1Actions.setGuide({ guide: null, fitToBounds: false }));
        myAlert(`Cleared Info from ${count} place${count !== 1 ? "s" : ""}.`);
      } else {
        myAlert("No Info to remove from the selected places.");
      }
      return;
    } else if (_place_operation === "BuildSchedule") {
      buildSchedulePlacesRef.current = places_selected;
      const startH = GuideSettings?.schedule_day_start ?? 9;
      const endH = GuideSettings?.schedule_day_end ?? 20;
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(startH, 0, 0, 0);
      // Single day by default — matches DestinationsScreen.js's own
      // defaultDestination (same day, day-start to day-end hour).
      const tomorrowEnd = new Date(tomorrow);
      tomorrowEnd.setHours(endH, 0, 0, 0);
      // A guide that already has destinations (e.g. from Import Itinerary or
      // Build My Trip) knows its own trip dates — default to the span they
      // cover instead of a single day, unless that span is already past.
      const destWithDates = (window.guide_obj?.destinations || []).filter((d) => d && d.city && d.dateStart && d.dateEnd);
      let defaultStart = tomorrow;
      let defaultEnd = tomorrowEnd;
      if (destWithDates.length > 0) {
        const toJS = (dt) => new Date(dt.year, dt.month - 1, dt.day, dt.hour, dt.minute);
        const starts = destWithDates.map((d) => toJS(d.dateStart));
        const ends = destWithDates.map((d) => toJS(d.dateEnd));
        const minStart = new Date(Math.min(...starts));
        const maxEnd = new Date(Math.max(...ends));
        if (!isNaN(minStart) && !isNaN(maxEnd) && maxEnd >= new Date()) {
          defaultStart = minStart;
          defaultEnd = maxEnd;
        }
      }
      // The selected places already imply which destination(s) this trip
      // covers — no separate destinations UI like TripAI/BuildMyTripWizard
      // have, so derive a {city, country, count} summary here (in first-
      // seen order) and hand it to the dialog to show and to name the
      // schedule from, instead of only ever assuming the guide's first city.
      const destinationSummary = [];
      const destIndexByKey = new Map();
      places_selected.forEach((p) => {
        const city = p.value?.City || "";
        const country = p.value?.Country || "";
        if (!city) return;
        const key = `${city}|${country}`;
        if (!destIndexByKey.has(key)) {
          destIndexByKey.set(key, destinationSummary.length);
          destinationSummary.push({ city, country, count: 0 });
        }
        destinationSummary[destIndexByKey.get(key)].count++;
      });
      const defaultName =
        destinationSummary.length === 0
          ? `${window.guide_obj.attributes.cities?.[0] || "Guide"} Trip`
          : destinationSummary.length === 1
          ? `${destinationSummary[0].city} Trip`
          : destinationSummary.length === 2
          ? `${destinationSummary[0].city} & ${destinationSummary[1].city} Trip`
          : `${destinationSummary.length}-City Trip`;
      setBuildScheduleDestinationSummary(destinationSummary);
      setBuildScheduleDefaultName(defaultName);
      setBuildScheduleDefaultDate(defaultStart);
      setBuildScheduleDefaultEndDate(defaultEnd);
      setBuildScheduleDialogShow(true);
      return;
    } else if (_place_operation === "SetLink") {
      places_selected.forEach((place_id) => {
        let place = window.guide_obj.places.getPlace(place_id.value.ID);
        if (place === null) return;
        place.Link = _link;
      });
    } else if (_place_operation === "FindPlaceInfo") {
      processInfoQueue(Places.slice());
      return;
    } else if (_place_operation === "FindOpeningTimes") {
      processOpeningHoursQueue(Places.slice());
      return;
    } else if (_place_operation === "ClearPhotos") {
      const placesToClear = Places.slice();
      setAlertModalTitle("Clear Photos");
      setAlertModalMessage(
        `Remove the photo from ${placesToClear.length} place${placesToClear.length === 1 ? "" : "s"} and show the default image instead?`
      );
      setAlertModalButtons([
        {
          Title: "Clear",
          Method: () => {
            placesToClear.forEach((item) => {
              const place = window.guide_obj.places.getPlace(item.value.ID);
              if (place !== null) PlaceMenu.clearPhoto(place);
            });
            UpdateDisplay();
            dispatch(slice1Actions.setGuide({ guide: null }));
          },
        },
        { Title: "Cancel", Method: null },
      ]);
      setAlertModalShow(true);
      return;
    } else if (_place_operation === "FixPhotos") {
      processFixPhotosQueue(Places.slice());
      return;
    } else if (_place_operation === "UpdateAllPhotos") {
      processUpdateAllPhotosQueue(Places.slice());
      return;
    } else if (_place_operation === "UpdateAllPhotosPaid") {
      // Real, ongoing cost (one paid photo search per place) — confirm the
      // count before spending it, unlike the free variant above. Vendor-
      // neutral wording deliberately: the underlying source (currently
      // HasData, previously Scrape.do — see autoSetPhoto's comment) is
      // still being actively compared, so this text shouldn't need
      // updating every time that changes.
      const placesToUpdate = Places.slice();
      setAlertModalTitle("Refresh Photos (Paid)");
      setAlertModalMessage(
        `This will run ${placesToUpdate.length} paid photo ${placesToUpdate.length === 1 ? "search" : "searches"} to look up real listing photos for ${placesToUpdate.length} place${placesToUpdate.length === 1 ? "" : "s"}. Continue?`
      );
      setAlertModalButtons([
        { Title: "Continue", Method: () => processUpdateAllPhotosPaidQueue(placesToUpdate) },
        { Title: "Cancel", Method: null },
      ]);
      setAlertModalShow(true);
      return;
    } else if (_place_operation === "AutoSetCountry") {
      processCountryQueue(Places.slice());
      return;
    } else if (_place_operation === "FixCategories") {
      processCategoryQueue(Places.slice());
      return;
    } else if (_place_operation === "Groups") {
      _selected_places_for_country = Places;
      setTimeout(() => selectGroups(), 100);
      return;
    } else if (_place_operation === "Categories") {
      _selected_places_for_country = Places;
      setTimeout(() => selectCategories(), 100);
      return;
    } else if (_place_operation === "SetCity") {
      _selected_places_for_country = Places;
      setTimeout(() => {
        setPromptModalTitle("Set City");
        setPromptModalMessage("Enter city name");
        setPromptModalButtons([
          { Title: "OK", Method: setCityCallback },
          { Title: "Cancel", Method: null },
        ]);
        setPromptModalInputs([{ name: "City", value: "" }]);
        setPromptModalShow(true);
      }, 100);
      return;
    } else if (_place_operation === "SetCountry") {
      _selected_places_for_country = Places;
      const countryItems = countries.map((c) => ({ label: c, value: c, name: c }));
      setTimeout(() => {
        dispatch(slice1Actions.setMultiselectCallback({ value: setCountryCallback }));
        dispatch(slice1Actions.setMultiselectItems({ value: countryItems }));
        dispatch(slice1Actions.setMultiselectSelectedItems({ value: [] }));
        dispatch(slice1Actions.setMultiselectMode({ value: "uni" }));
        dispatch(slice1Actions.setMultiselectSearchText({ value: "" }));
        dispatch(slice1Actions.setMultiselectTitle({ value: "Select Country" }));
        dispatch(slice1Actions.setShowMultiselect({ value: true }));
      }, 100);
      return;
    }

    window.guide_obj.places.setPlaceAttributes(AllPlacesSubList, Places, _place_operation);
    UpdateDisplay();
  };

  const setCityCallback = (inputs) => {
    if (inputs === null || inputs.length === 0) return;
    const city = inputs[0].value;
    _selected_places_for_country.forEach((placeItem) => {
      const place = window.guide_obj.places.getPlace(placeItem.value.ID);
      if (place !== null) place.City = city;
    });
    UpdateDisplay();
  };

  const checkImageUrl = (url) =>
    new Promise((resolve) => {
      if (!url || url === "") { resolve(false); return; }
      const img = new Image();
      const timer = setTimeout(() => { img.src = ""; resolve(false); }, 8000);
      img.onload = () => { clearTimeout(timer); resolve(true); };
      img.onerror = () => { clearTimeout(timer); resolve(false); };
      img.src = url;
    });

  const checkImageUrlCors = (url) =>
    new Promise((resolve) => {
      if (!url || url === "") { resolve(false); return; }
      const img = new Image();
      img.crossOrigin = "anonymous";
      // Bust the browser cache so it makes a fresh CORS request rather than
      // reusing a cached non-CORS response.
      const sep = url.includes("?") ? "&" : "?";
      const timer = setTimeout(() => { img.src = ""; resolve(false); }, 8000);
      img.onload = () => { clearTimeout(timer); resolve(true); };
      img.onerror = () => { clearTimeout(timer); resolve(false); };
      img.src = `${url}${sep}_cors=${Date.now()}`;
    });

  let _photoQueue = [];
  let _photoQueueIndex = 0;
  let _countryQueue = [];
  let _countryFinish = () => {};
  let _countryQueueIndex = 0;
  let _categoryQueue = [];
  let _categoryQueueIndex = 0;

  // Bounded-concurrency worker pool — runs `processItem` for up to
  // ENRICHMENT_CONCURRENCY places at once instead of one at a time with an
  // artificial pause between each (the old approach). Network latency is
  // what actually dominates each lookup (Wikipedia is two sequential calls
  // per place; Google Places is one), not any real per-place computation,
  // so this is a straightforward multiplier on throughput. Kept
  // deliberately modest rather than unbounded, to stay well clear of rate
  // limits on either Wikipedia's public API or the Google Places proxy's
  // own quota. Progress is shown as a plain count rather than the previous
  // "n / total: place name" — attributing one name to several places
  // finishing concurrently would be misleading.
  const ENRICHMENT_CONCURRENCY = 4;

  // stepLabel, when passed (e.g. "Step 1/2"), is prefixed onto the progress
  // text — used when two of these queues run back to back (see
  // runScheduleEnrichment below) so the count resetting to 0 for the second
  // pass doesn't read as the first pass restarting.
  const runConcurrentQueue = (items, processItem, setLoading, setProgress, onComplete, stepLabel) => {
    const total = items.length;
    const prefix = stepLabel ? `${stepLabel}, ` : "";
    if (total === 0) {
      if (onComplete) onComplete();
      return;
    }
    setLoading(true);
    setProgress(`${prefix}0 / ${total}`);
    let nextIndex = 0;
    let completed = 0;
    const runWorker = async () => {
      while (nextIndex < total) {
        const item = items[nextIndex++];
        // A single item throwing (e.g. an unexpected shape) must not sink
        // the whole worker pool — an uncaught rejection here would reject
        // the outer Promise.all, meaning .then() below (which clears
        // loading/progress) never runs and the UI looks stuck forever.
        try {
          await processItem(item);
        } catch (_) {
          // skip this item, keep the pool going
        }
        completed++;
        setProgress(`${prefix}${completed} / ${total}`);
      }
    };
    const workerCount = Math.min(ENRICHMENT_CONCURRENCY, total);
    Promise.all(Array.from({ length: workerCount }, runWorker)).then(() => {
      setLoading(false);
      setProgress("");
      UpdateDisplay();
      dispatch(slice1Actions.setGuide({ guide: null }));
      if (onComplete) onComplete();
    });
  };

  // placeItem may be either a raw Place object (buildScheduleFromGuidePlaces'
  // enrichment tail passes window.guide_obj.places.placesList directly) or a
  // multiselect item shaped {value: Place} (the standalone "Find Info"/"Find
  // Opening Times" menu actions pass their selection list) — accept both.
  const resolvePlaceItem = (placeItem) => window.guide_obj.places.getPlace((placeItem.value ?? placeItem).ID);

  const processInfoQueue = (places, onComplete, stepLabel) => {
    runConcurrentQueue(places, async (placeItem) => {
      const place = resolvePlaceItem(placeItem);
      if (place === null) return;
      // Skip places that already have our own Wikipedia-sourced content —
      // detected by the exact marker link this same lookup inserts below,
      // not just "any Info", so places with unrelated user-written notes
      // still get a real Wikipedia lookup instead of being skipped. Lets
      // this run safely across a whole guide repeatedly (e.g. after adding
      // a few new places) without re-fetching for places already done.
      if (place.Info && place.Info.includes("See more on Wikipedia")) return;
      const query = [place.Name, place.City, place.Country].filter(Boolean).join(", ");
      try {
        // Step 1: search Wikipedia for the best matching article title
        const searchUrl = `https://en.wikipedia.org/w/api.php?action=query&list=search&srsearch=${encodeURIComponent(query)}&format=json&origin=*&srlimit=1`;
        const searchResp = await fetch(searchUrl);
        const searchData = await searchResp.json();
        const results = searchData?.query?.search;
        if (results && results.length > 0) {
          const title = results[0].title;
          // Step 2: get the page summary from the REST API
          const summaryUrl = `https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(title)}`;
          const summaryResp = await fetch(summaryUrl);
          const summary = await summaryResp.json();
          const extract = summary.extract || "";
          const pageUrl = summary.content_urls?.desktop?.page || `https://en.wikipedia.org/wiki/${encodeURIComponent(title)}`;
          if (extract) {
            place.Info = `${extract}<br><a href="urllink:${pageUrl}">... See more on Wikipedia</a>`;
          }
        }
      } catch (_) {
        // silently skip on error
      }
    }, setInfoSearchLoading, setInfoSearchProgress, onComplete, stepLabel);
  };

  const processOpeningHoursQueue = (places, onComplete, stepLabel) => {
    // One notice for the whole run if today's lookup limit is reached partway
    // through — not silence, and not one per place. See Common/GateLimit.js.
    clearDailyLimitNotice();
    const finished = () => {
      if (takeDailyLimitNotice()) showLimitNotice("hoursOnly");
      if (onComplete) onComplete();
    };
    runConcurrentQueue(places, async (placeItem) => {
      const place = resolvePlaceItem(placeItem);
      if (place === null) return;
      // Skip places that already have real opening-hours data — same
      // "safe to re-run across the whole guide" reasoning as the Wikipedia
      // skip above. hasOpeningHoursData (not just truthiness) matters here:
      // every place starts with an empty default OpeningHours skeleton, so
      // a plain existence check would skip everything on the first run too.
      if (GooglePlaces.hasOpeningHoursData(place.OpeningHours)) return;
      try {
        await new Promise((resolve) => {
          GooglePlaces.fetchOpeningHours(place, (hours) => {
            if (hours) place.OpeningHours = hours;
            resolve();
          });
        });
      } catch (_) {
        // silently skip on error
      }
    }, setOpeningHoursSearchLoading, setOpeningHoursSearchProgress, finished, stepLabel);
  };

  // "Fix Photos" — a single repair pass replacing the old three-step chain
  // (Auto Set Photo → Update Bad Photo Links → Update CORS-Blocked Photos).
  // Per place: no photo → search; broken link → search; loads fine but fails
  // a canvas-use CORS check → CORS-aware search. A working photo is left
  // untouched — this is a repair tool, not a force-refresh.
  const processFixPhotosQueue = (places) => {
    _photoQueue = places;
    _photoQueueIndex = 0;
    setPhotoSearchLoading(true);
    setPhotoSearchProgress(`0 / ${places.length}`);
    processNextFixPhotosSearch();
  };

  const processNextFixPhotosSearch = async () => {
    if (_photoQueueIndex >= _photoQueue.length) {
      setPhotoSearchLoading(false);
      setPhotoSearchProgress("");
      UpdateDisplay();
      dispatch(slice1Actions.setGuide({ guide: null }));
      return;
    }
    const placeItem = _photoQueue[_photoQueueIndex];
    setPhotoSearchProgress(`${_photoQueueIndex + 1} / ${_photoQueue.length}: ${placeItem.label}`);
    const place = window.guide_obj.places.getPlace(placeItem.value.ID);
    if (place !== null && !PlaceMenu.hasNoGoodPhoto(place)) {
      const url = place.Photo_m || place.Photo;
      if (!url) {
        await new Promise((resolve) => PlaceMenu.autoSetPhoto(place, resolve, { allowPaidFallback: false, licenseSafe: true }));
      } else {
        const loadsNormally = await checkImageUrl(url);
        if (!loadsNormally) {
          await new Promise((resolve) => PlaceMenu.autoSetPhoto(place, resolve, { allowPaidFallback: false, licenseSafe: true }));
        } else {
          const corsOk = await checkImageUrlCors(url);
          if (!corsOk) await autoSetPhotoCorsOk(place, { allowPaidFallback: false });
        }
      }
    }
    _photoQueueIndex++;
    setTimeout(processNextFixPhotosSearch, 300);
  };

  // "Refresh Photos" — a force-refresh, unlike "Fix Photos" above (which
  // only touches places with no/broken/CORS-blocked photos and leaves a
  // working-but-wrong one alone). Re-searches every selected place
  // regardless of its current photo — e.g. after reordering the
  // Wikimedia/manifest/Google photo-source priority, to reassess a whole
  // guide's photo quality at once rather than one place at a time via
  // "Auto Update Photo". Restores bulk force-refresh, lost when the old
  // three-step photo-fix chain was consolidated into "Fix Photos". Added
  // 2026-08-29 — see memory: project_cost_revenue_viability.
  const processUpdateAllPhotosQueue = (places) => {
    _photoQueue = places;
    _photoQueueIndex = 0;
    setPhotoSearchLoading(true);
    setPhotoSearchProgress(`0 / ${places.length}`);
    processNextUpdateAllPhotosSearch();
  };

  const processNextUpdateAllPhotosSearch = async () => {
    if (_photoQueueIndex >= _photoQueue.length) {
      setPhotoSearchLoading(false);
      setPhotoSearchProgress("");
      UpdateDisplay();
      dispatch(slice1Actions.setGuide({ guide: null }));
      return;
    }
    const placeItem = _photoQueue[_photoQueueIndex];
    setPhotoSearchProgress(`${_photoQueueIndex + 1} / ${_photoQueue.length}: ${placeItem.label}`);
    const place = window.guide_obj.places.getPlace(placeItem.value.ID);
    if (place !== null && !PlaceMenu.hasNoGoodPhoto(place)) {
      // Free-only (no billed Custom Search) — a bulk force-refresh over a
      // user's whole selection is the highest cost-exposure case in the app
      // (see memory: project_cost_revenue_viability), so this trades away
      // the paid fallback's occasional better match for a hard cap on cost.
      await new Promise((resolve) => PlaceMenu.autoSetPhoto(place, resolve, { allowPaidFallback: false, licenseSafe: true }));
    }
    _photoQueueIndex++;
    setTimeout(processNextUpdateAllPhotosSearch, 300);
  };

  // "Refresh Photos (Paid)" — same force-refresh as the free variant
  // above, but with allowPaidFallback left true so Scrape.do's real
  // listing-photo search runs too, not just Wikimedia/FeaturedPlaceImages.
  // Real, ongoing cost (one Scrape.do search per place, confirmed before
  // this runs — see the UpdateAllPhotosPaid dispatch case), unlike the free
  // variant, which deliberately caps cost at zero. Added 2026-09-05 once
  // Scrape.do was verified working — see memory:
  // project_cost_revenue_viability.
  const processUpdateAllPhotosPaidQueue = (places) => {
    _photoQueue = places;
    _photoQueueIndex = 0;
    setPhotoSearchLoading(true);
    setPhotoSearchProgress(`0 / ${places.length}`);
    processNextUpdateAllPhotosPaidSearch();
  };

  const processNextUpdateAllPhotosPaidSearch = async () => {
    if (_photoQueueIndex >= _photoQueue.length) {
      setPhotoSearchLoading(false);
      setPhotoSearchProgress("");
      UpdateDisplay();
      dispatch(slice1Actions.setGuide({ guide: null }));
      return;
    }
    const placeItem = _photoQueue[_photoQueueIndex];
    setPhotoSearchProgress(`${_photoQueueIndex + 1} / ${_photoQueue.length}: ${placeItem.label}`);
    const place = window.guide_obj.places.getPlace(placeItem.value.ID);
    if (place !== null) {
      await new Promise((resolve) => PlaceMenu.autoSetPhoto(place, resolve, { allowPaidFallback: true }));
    }
    _photoQueueIndex++;
    setTimeout(processNextUpdateAllPhotosPaidSearch, 300);
  };

  // "Fill Missing City & Country" — only touches selected places missing a
  // City and/or Country; existing values are never overwritten. Free step
  // first: borrow from the nearest place in the guide that has the field,
  // if within FILL_NEARBY_MAX_M. Only what's still missing is reverse-
  // geocoded (billed Google Geocoding), after the user confirms the count.
  // Wanderlist's country/continent stats need every place to have a Country.
  const FILL_NEARBY_MAX_M = 25000;
  const hasCoords = (p) =>
    typeof p.Lat === "number" && !isNaN(p.Lat) && typeof p.Longi === "number" && !isNaN(p.Longi) &&
    !(p.Lat === 0 && p.Longi === 0);
  const isBlank = (v) => !v || String(v).trim() === "";
  let _fillStats = { free: 0, geocoded: 0, unresolved: 0, noCoords: 0 };

  const processCountryQueue = (places) => {
    const guidePlaces = (window.guide_obj.places.placesList || []).filter((p) => p != null && hasCoords(p));
    const nearest = (place, field) => {
      let best = null;
      let bestD = FILL_NEARBY_MAX_M;
      for (const g of guidePlaces) {
        if (g === place || isBlank(g[field])) continue;
        const d = Common.haversineMeters(place.Lat, place.Longi, g.Lat, g.Longi);
        if (d <= bestD) { bestD = d; best = g; }
      }
      return best;
    };
    _fillStats = { free: 0, geocoded: 0, unresolved: 0, noCoords: 0 };
    const needsLookup = [];
    places.forEach((item) => {
      const place = window.guide_obj.places.getPlace(item.value.ID);
      if (place === null || (!isBlank(place.City) && !isBlank(place.Country))) return;
      if (!hasCoords(place)) { _fillStats.noCoords++; return; }
      // Only borrow a city from a neighbor in the same country (or when the
      // country is unknown), so a border-side place doesn't get a foreign city.
      const donorCountry = isBlank(place.Country) ? nearest(place, "Country") : null;
      if (donorCountry) { place.Country = donorCountry.Country; }
      const donorCity = isBlank(place.City) ? nearest(place, "City") : null;
      if (donorCity && (isBlank(place.Country) || donorCity.Country === place.Country)) {
        place.City = donorCity.City;
      }
      if (!isBlank(place.City) && !isBlank(place.Country)) _fillStats.free++;
      else needsLookup.push(item);
    });
    const finish = () => {
      setCountrySearchLoading(false);
      setCountrySearchProgress("");
      UpdateDisplay();
      dispatch(slice1Actions.setGuide({ guide: null }));
      const filled = _fillStats.free + _fillStats.geocoded;
      const parts = [`Filled ${filled} place${filled === 1 ? "" : "s"} (${_fillStats.free} from nearby places, ${_fillStats.geocoded} looked up).`];
      if (_fillStats.unresolved > 0) parts.push(`${_fillStats.unresolved} couldn't be determined.`);
      if (_fillStats.noCoords > 0) parts.push(`${_fillStats.noCoords} skipped — no valid location.`);
      if (filled === 0 && _fillStats.unresolved === 0 && _fillStats.noCoords === 0) parts[0] = "Every selected place already has a city and country.";
      setAlertModalTitle("Fill Missing City & Country");
      setAlertModalMessage(parts.join(" "));
      setAlertModalButtons([{ Title: "OK", Method: null }]);
      setAlertModalShow(true);
    };
    if (needsLookup.length === 0) {
      finish();
      return;
    }
    _countryQueue = needsLookup;
    _countryQueueIndex = 0;
    _countryFinish = finish;
    setAlertModalTitle("Fill Missing City & Country");
    setAlertModalMessage(
      `${needsLookup.length} place${needsLookup.length === 1 ? "" : "s"} still ${needsLookup.length === 1 ? "needs" : "need"} a location lookup${_fillStats.free > 0 ? ` (${_fillStats.free} already filled from nearby places)` : ""}. Continue?`
    );
    setAlertModalButtons([
      {
        Title: "Continue",
        Method: () => {
          setCountrySearchLoading(true);
          setCountrySearchProgress(`0 / ${needsLookup.length}`);
          processNextCountrySearch();
        },
      },
      { Title: "Skip Lookup", Method: () => setTimeout(finish, 0) },
    ]);
    setAlertModalShow(true);
  };

  const processNextCountrySearch = async () => {
    if (_countryQueueIndex >= _countryQueue.length) {
      _countryFinish();
      return;
    }
    const placeItem = _countryQueue[_countryQueueIndex];
    setCountrySearchProgress(`${_countryQueueIndex + 1} / ${_countryQueue.length}: ${placeItem.label}`);
    const place = window.guide_obj.places.getPlace(placeItem.value.ID);
    if (place === null) {
      _countryQueueIndex++;
      setTimeout(processNextCountrySearch, 100);
      return;
    }
    let city = "";
    let country = "";
    try {
      const res = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${place.Lat},${place.Longi}&key=${process.env.REACT_APP_GOOGLE_MAPS_GEOCODING_KEY}`
      );
      const json = await res.json();
      for (const result of json.results || []) {
        const cityComponent = (result.address_components || []).find((c) => c.types.includes("locality"));
        const countryComponent = (result.address_components || []).find((c) => c.types.includes("country"));
        if (cityComponent && !city) city = cityComponent.long_name;
        if (countryComponent && !country) country = countryComponent.long_name;
        if (city && country) break;
      }
    } catch (e) {
      console.log("Fill Missing City & Country error:", e.message);
    }
    if (isBlank(place.Country) && country) place.Country = country;
    if (isBlank(place.City) && city) place.City = city;
    if (!isBlank(place.City) && !isBlank(place.Country)) _fillStats.geocoded++;
    else _fillStats.unresolved++;
    _countryQueueIndex++;
    setTimeout(processNextCountrySearch, 300);
  };

  // Live type-based lookup, third and last fallback in the chain below —
  // added 2026-09-24 now that PLACES_PROVIDER defaults to OpenWeb Ninja: a
  // narrow-radius single-place search (same query pattern as
  // GooglePlaces.fetchOpeningHours) returns the place's own real
  // types/subtypes, so this reuses mapGoogleTypesToCategoryId on real data
  // instead of guessing from the Name alone. Previously this tool had NO
  // live-API fallback at all (deliberately, back when Google's own Places
  // API was the only option and billed per call) — that made it miss any
  // generically-named place whose Name doesn't happen to contain a
  // category word, e.g. "Birds of Eden" (a real report investigated the
  // same day) contains neither "zoo" nor "wildlife" nor "attraction", so
  // the Name-only fallback below could never have caught it, no matter how
  // complete the taxonomy is. A scraper-backed provider makes this cheap
  // enough to always attempt now.
  const searchRealPlaceTypes = (place) => new Promise((resolve) => {
    const query = [place.Name, place.City, place.Country].filter(Boolean).join(", ");
    if (!query) { resolve(null); return; }
    const gp = new GooglePlaces();
    gp.searchPlacesNewAPI(query, null, place.Lat, place.Longi, 200, (results) => {
      resolve(results && results.length > 0 && Array.isArray(results[0].Category) ? results[0].Category : null);
    }, true);
  });

  // "Fix Categories" — repair tool, not a force-refresh: only touches places
  // with no category at all (an already-categorized place, right or wrong,
  // is left alone — use Categories > Set to Categories to override one
  // deliberately). Three sources, in order: (1) the FeaturedPlaceImages
  // manifest, which carries a curated category name per entry (see
  // scripts/addCategoriesToManifest.js) for any place that matches an
  // existing Featured Guide place by country/city/name — free, no API call;
  // (2) a fuzzy match of the place's own Name against the AICategories/
  // AIBaseCategories taxonomy (works when a name already contains its own
  // category word, e.g. "City Museum" contains "museum") — also free; (3)
  // searchRealPlaceTypes above — a real, cheap-but-live lookup, only run
  // when both free sources miss.
  const processCategoryQueue = (places) => {
    _categoryQueue = places;
    _categoryQueueIndex = 0;
    setCategorySearchLoading(true);
    setCategorySearchProgress(`0 / ${places.length}`);
    processNextCategorySearch();
  };

  const processNextCategorySearch = async () => {
    if (_categoryQueueIndex >= _categoryQueue.length) {
      setCategorySearchLoading(false);
      setCategorySearchProgress("");
      UpdateDisplay();
      dispatch(slice1Actions.setGuide({ guide: null }));
      return;
    }
    const placeItem = _categoryQueue[_categoryQueueIndex];
    setCategorySearchProgress(`${_categoryQueueIndex + 1} / ${_categoryQueue.length}: ${placeItem.label}`);
    const place = window.guide_obj.places.getPlace(placeItem.value.ID);
    if (place !== null && (!place.Category || place.Category.length === 0)) {
      let categoryId = null;
      const manifestMatch = await getFeaturedPlaceMatch(place.Country, place.City, place.Name);
      if (manifestMatch?.category) {
        // The manifest gives a category NAME (curated text, not an ID — see
        // scripts/addCategoriesToManifest.js), so this one path still needs
        // a name-based lookup against the guide's own category list.
        const match = window.guide_obj.categories.find(
          (c) => (c.name || "").toLowerCase() === manifestMatch.category.toLowerCase()
        );
        if (match) categoryId = match.id;
      } else {
        let aiCategories = DEFAULT_AI_CATEGORIES;
        let aiBaseCategories = DEFAULT_AI_BASE_CATEGORIES;
        try {
          const parsed = JSON.parse(AppSettings.AICategories);
          if (Array.isArray(parsed) && parsed.length > 0) aiCategories = parsed;
        } catch (e) { /* keep default */ }
        try {
          const parsed = JSON.parse(AppSettings.AIBaseCategories);
          if (Array.isArray(parsed) && parsed.length > 0) aiBaseCategories = parsed;
        } catch (e) { /* keep default */ }
        // Use mapGoogleTypesToCategoryId's returned ID DIRECTLY (same
        // pattern BuildMyTripWizard.js already uses correctly) — only
        // checking it actually exists in THIS guide's own category list,
        // rather than round-tripping ID -> taxonomy label -> re-lookup by
        // name. Found 2026-09-24 investigating a real "Fix Categories
        // didn't fix it" report (Maboneng Precinct): the round-trip broke
        // whenever multiple taxonomy labels map to the same id (very
        // common, e.g. "Shopping"/"Shopping Center" both -> 110) — it
        // always picked the FIRST label for that id ("Shopping Center"),
        // which isn't necessarily this guide's actual category name at
        // that id ("Shopping"), so the final name-based re-lookup silently
        // failed even though the correct id was already known.
        const idExistsInGuide = (id) => !!id && window.guide_obj.categories.some((c) => c.id === id);
        const nameId = mapGoogleTypesToCategoryId([place.Name], aiCategories, aiBaseCategories);
        categoryId = idExistsInGuide(nameId) ? nameId : null;
        if (!categoryId) {
          const realTypes = await searchRealPlaceTypes(place);
          if (realTypes && realTypes.length > 0) {
            const typeId = mapGoogleTypesToCategoryId(realTypes, aiCategories, aiBaseCategories);
            categoryId = idExistsInGuide(typeId) ? typeId : null;
          }
        }
      }
      if (categoryId) place.Category = [categoryId];
    }
    _categoryQueueIndex++;
    setTimeout(processNextCategorySearch, 300);
  };

  // ── CORS-aware photo replacement ────────────────────────────────────────────

  const searchPhotosGoogle = (query) =>
    new Promise(resolve => PlaceMenu.searchPlacePhotoGoogle(query, resolve));

  // Only images with no usage conditions (public domain / CC0) — see
  // PlaceMenu._autoSetPhotoLicenseSafe.
  const searchPhotosWiki = (query, place) =>
    new Promise(resolve => WikiMedia.searchImagesForPlace(query, place?.Lat, place?.Longi, resolve, undefined, { unrestrictedOnly: true, matchName: place?.Name, radiusKm: searchRadiusKm(place?.Name, PlaceMenu._categoryNames(place || {})) }));

  // static.flickr.com sends no CORS headers and serves http:// only (mixed
  // content on this https page) — route through the same proxy
  // ExportVideoModal uses, which serves https and adds
  // Access-Control-Allow-Origin: *, so results actually pass checkImageUrlCors.
  const FLICKR_IMG_PROXY = `${API_BASE_URL}/img_proxy.php?url=`;

  const searchPhotosFlickr = (place, query) =>
    new Promise(resolve => {
      const hasCoords =
        typeof place.Lat === "number" && !isNaN(place.Lat) &&
        typeof place.Longi === "number" && !isNaN(place.Longi) &&
        !(place.Lat === 0 && place.Longi === 0);
      const _Flickr = new Flickr();
      const callback = (photos) => {
        if (!photos) { resolve([]); return; }
        const results = photos
          .filter(p => typeof p.title !== "undefined")
          .map(p => {
            const base = `http://farm${p.farm}.static.flickr.com/${p.server}/${p.id}_${p.secret}`;
            // "_b" (1024px) isn't guaranteed to exist for every photo (only
            // generated for sufficiently high-res originals); "_z" (640px)
            // reliably is, for both fields.
            const url = FLICKR_IMG_PROXY + encodeURIComponent(`${base}_z.jpg`);
            return { url, url_m: url };
          });
        resolve(results);
      };
      if (hasCoords) {
        _Flickr.searchPhotos("", query, "20", callback, {
          center: { latitude: place.Lat, longitude: place.Longi },
          radius: 32, // Flickr's documented max radius (km)
        }, Flickr.UNRESTRICTED_LICENSES);
      } else {
        _Flickr.searchPhotos("-180,-90,180,90", query, "20", callback, undefined, Flickr.UNRESTRICTED_LICENSES);
      }
    });

  const autoSetPhotoCorsOk = async (place, { allowPaidFallback = true } = {}) => {
    const query = PlaceMenu.buildPhotoSearchQuery(place);

    // Try each Google result in order until one passes the CORS test —
    // skipped entirely for free-only callers (see allowPaidFallback on
    // PlaceMenu.autoSetPhoto; same billed-Custom-Search cost concern, see
    // memory: project_cost_revenue_viability).
    if (allowPaidFallback) {
      const googleResults = await searchPhotosGoogle(query);
      for (const img of googleResults) {
        if (await checkImageUrlCors(img.url_m || img.url)) {
          place.Photo   = img.url;
          place.Photo_m = img.url_m;
          return true;
        }
      }
    }

    // Fall back to Wikimedia (upload.wikimedia.org sends Access-Control-Allow-Origin: *)
    const wikiResults = await searchPhotosWiki(query, place);
    for (const img of wikiResults) {
      if (await checkImageUrlCors(img.url_m || img.url)) {
        place.Photo   = img.url;
        place.Photo_m = img.url_m;
        return true;
      }
    }

    // Fall back to Flickr, centered on the place's own coordinates. Use the
    // bare name rather than the City/Country-augmented `query` used above —
    // Flickr's search is already geo-constrained to a 32km radius, so also
    // requiring the text to match City/Country on top of that tends to
    // over-narrow the results to nothing (confirmed: the same place found
    // results via ImageDialog's manual search, which searches name-only).
    const flickrResults = await searchPhotosFlickr(place, place.Name);
    for (const img of flickrResults) {
      if (await checkImageUrlCors(img.url_m || img.url)) {
        place.Photo   = img.url;
        place.Photo_m = img.url_m;
        return true;
      }
    }

    // Last resort: Mapillary street-level imagery near the place's exact
    // coordinates — covers streets/addresses that never get a "named
    // landmark" photo from any of the sources above.
    // Commented out until the Mapillary developer account is confirmed
    // (REACT_APP_MAPILLARY_ACCESS_TOKEN pending) — re-enable once that lands.
    // const mapillaryResults = await new Promise(resolve =>
    //   Mapillary.searchImages(place.Lat, place.Longi, 100, resolve)
    // );
    // for (const img of mapillaryResults) {
    //   if (await checkImageUrlCors(img.url_m || img.url)) {
    //     place.Photo   = img.url;
    //     place.Photo_m = img.url_m;
    //     return true;
    //   }
    // }

    return false;
  };

  const setCountryCallback = (selection) => {
    if (selection === null || selection.length === 0) return;
    const country = selection[0].value;
    _selected_places_for_country.forEach((placeItem) => {
      const place = window.guide_obj.places.getPlace(placeItem.value.ID);
      if (place !== null) place.Country = country;
    });
    UpdateDisplay();
  };

  const createEventNew = (place_id) => {
    let schedule = "";
    let scheds = window.guide_obj.schedule.geScheduleNames();
    for (let s = 1; s < 99999; s++) {
      schedule = `Itinerary${s}`;
      let found = false;
      for (let x = 1; x < scheds.length; x++) {
        if (schedule === scheds[x].label) found = true;
      }
      if (found === false) break;
    }

    let dateGuide = DateTime.now({ zone: "utc" });
    dateGuide = dateGuide.set({ hour: 11 });
    dateGuide = dateGuide.set({ minutes: 0 });
    let dayDate = dateGuide;
    let last_place = null;
    let rounding_to = user_info.event_time_rounding;
    if (typeof rounding_to === "undefined") rounding_to = 15;
    let PlaceOrderByDistance = Common.getPlaceOrderByDistance(TempValue, window.guide_obj);
    let start_index = 0;
    let end_index = PlaceOrderByDistance.length;
    let thisDay = dayDate;
    let firstEventOfDay = true;
    if (place_id !== null) {
      const index = PlaceOrderByDistance.findIndex((x) => (x.ID === place_id));
      if (index !== -1) {
        start_index = index;
        for (let i = start_index; i < end_index; i++) {
          dayDate = addEvent(PlaceOrderByDistance[i], schedule, dayDate, last_place, rounding_to, firstEventOfDay);
          if (dayDate.day !== thisDay.day) {
            thisDay = dayDate;
            firstEventOfDay = true;
          } else firstEventOfDay = false;
          last_place = PlaceOrderByDistance[i];
        }
        end_index = index - 1;
      }
    }
    for (let i = start_index; i < end_index; i++) {
      dayDate = addEvent(PlaceOrderByDistance[i], schedule, dayDate, last_place, rounding_to, firstEventOfDay);
      if (dayDate.day !== thisDay.day) {
        thisDay = dayDate;
        firstEventOfDay = true;
      } else firstEventOfDay = false;
      last_place = PlaceOrderByDistance[i];
    }
    dispatch(slice1Actions.setScheduleName({ value: schedule }));
    dispatch(slice1Actions.setGuide({ guide: null, fitToBounds: true }));
  };
  const addEvent = (place, Itinerary, dayDate, last_place, rounding_to, firstEventOfDay) => {
    let event = new Appointment(window.guide_obj.schedule);
    event.Subject = place.Name;
    event.Body = "";
    event.Itinerary = Itinerary;
    event.Places = place.ID;
    let travel_time = last_place !== null ? Common.estimateTravelTimeMinutes(last_place, place, rounding_to) : 0;
    //let total_time =
    if (!firstEventOfDay || travel_time > 30) dayDate = dayDate.plus({ minutes: travel_time });
    if (dayDate.hour + travel_time / 60 + place.Duration / 60 > 20) {
      dayDate = dayDate.plus({ hours: 24 });
      dayDate = dayDate.set({ hour: 11 });
      dayDate = dayDate.set({ minutes: 0 });
      event.Start = dayDate.toISO();
      dayDate = dayDate.plus({ minutes: place.Duration });
      event.End = dayDate.toISO();
      window.guide_obj.schedule.addEvent(event);
    } else {
      event.Start = dayDate.toISO();
      dayDate = dayDate.plus({ minutes: place.Duration });
      event.End = dayDate.toISO();
      window.guide_obj.schedule.addEvent(event);
    }
    if (dayDate.hour >= 19) {
      dayDate = dayDate.plus({ hours: 24 });
      dayDate = dayDate.set({ hour: 11 });
      dayDate = dayDate.set({ minutes: 0 });
    }
    return dayDate;
  };

  // Opening-hours-aware placement, matching Build My Trip's identical
  // function — see Schedule.js's addEventOpeningHoursAware for the actual
  // implementation. Was addEventOptimizedLite (no opening-hours check at
  // all, just an unconditional roll to tomorrow if today's hour window
  // doesn't fit) until 2026-09-16, when "Build Trip from Places" was given
  // the same real opening-hours awareness Build My Trip already has: shift
  // within the day if closed now but opens later, or probe forward days
  // (capped) if the day just doesn't work at all, instead of blindly
  // committing to a possibly-still-closed tomorrow.
  const addEventOpeningHoursAware = (place, itinerary, dayDate, lastPlace, roundingTo, firstEventOfDay, startH, endH, placesModel, maxAllowedDate, paceMultiplier, hardEndTime) =>
    window.guide_obj.schedule.addEventOpeningHoursAware(place, itinerary, dayDate, lastPlace, roundingTo, firstEventOfDay, startH, endH, placesModel, maxAllowedDate, paceMultiplier, hardEndTime);

  // "Build Trip from Places" — replaces the old two-step chain of
  // manually multiselecting places into "Create Events From Places" and then
  // separately running "Optimize by Location" / "Recalculate Times" from the
  // Schedule panel's menu, and also absorbs "Create Events From Places"
  // itself (see BuildScheduleFromPlaces/BuildScheduleDialog below). Orders
  // the chosen places by travel distance and builds day-bounded,
  // distance-aware timing in one pass. Ported from andiamo-places'
  // PlacesScreen.js (buildScheduleFromGuidePlaces).
  // places_selected, when passed (from the "Build Trip from Places"
  // multiselect step below), lets the user build from a subset instead of
  // every place in the guide — this replaced the old, separate "Create
  // Events From Places" feature rather than keeping both, since picking a
  // subset here now runs through the same day-bounded, settings-aware
  // scheduler as building from everything.
  //
  // scheduleOptions (from BuildScheduleDialog) additionally lets the result
  // target an existing schedule (append) instead of always creating a new
  // one, and lets the start date be chosen instead of always defaulting to
  // tomorrow — the two capabilities "Create Events From Places" had that
  // this feature didn't, closing the gap that was the reason to keep both.
  const buildScheduleFromGuidePlaces = (places_selected, scheduleOptions) => {
    const guidePlaces = places_selected
      ? places_selected.map((p) => window.guide_obj.places.getPlace(p.value.ID)).filter((p) => p !== null)
      : window.guide_obj?.places?.placesList || [];
    if (guidePlaces.length === 0) {
      myAlert("Add some places to the guide first.");
      return;
    }
    // Places with no visit length of their own get an estimate first.
    Common.estimateUnsetDurations(guidePlaces, window.guide_obj.categories);

    let scheduleName;
    if (scheduleOptions && scheduleOptions.isNewSchedule === false) {
      // Appending to an existing schedule — use its exact label, no dedup.
      scheduleName = scheduleOptions.scheduleName;
      // Replace "X" (BuildScheduleDialog): empty the schedule first, so the
      // build below fills it from scratch instead of appending.
      if (scheduleOptions.replaceExisting) window.guide_obj.schedule.deleteAllEvents(scheduleName);
    } else {
      const baseSchedule = (scheduleOptions && scheduleOptions.scheduleName) || `${window.guide_obj.attributes.cities?.[0] || "Guide"} Trip`;
      const scheds = window.guide_obj.schedule.geScheduleNames();
      scheduleName = baseSchedule;
      for (let s = 0; s < 99999; s++) {
        scheduleName = `${baseSchedule}${s === 0 ? "" : " " + s}`;
        if (scheds.findIndex((x) => x.label === scheduleName) === -1) break;
      }
    }

    const ordered = Common.getPlaceOrderByDistance(null, window.guide_obj, guidePlaces);
    const startH = GuideSettings?.schedule_day_start ?? 9;
    const endH = GuideSettings?.schedule_day_end ?? 20;
    const rounding_to = user_info.event_time_rounding ?? 15;
    // Keep the chosen date's local wall-clock components but tag them UTC
    // (Common.LuxonDateFromJSDate does exactly this — the same helper
    // TripAI.js's own date pickers use) — Schedule display code (e.g.
    // SchedulePanel.js's day filter: DateTime.fromISO(e.Start, { zone: "utc"
    // })) reads Event.Start/End as UTC-tagged wall-clock time, so a real
    // local→UTC conversion here would shift the actual numbers by the user's
    // UTC offset and pick the wrong calendar day.
    const startDate = (scheduleOptions && scheduleOptions.startDate) || (() => {
      const d = new Date();
      d.setDate(d.getDate() + 1);
      return d;
    })();
    // The exact picked Start datetime is used as-is for the very first
    // event (addEventOptimizedLite's isVeryFirstEvent bypass) — same
    // precision TripAI.js/BuildMyTripWizard.js use for a destination's
    // dateStart, replacing the old always-startH default.
    let dayDate = Common.LuxonDateFromJSDate(startDate);
    let thisDay = dayDate;
    let firstEventOfDay = true;
    let lastPlace = null;
    // End (BuildScheduleDialog's End field) is a hard ceiling — nothing gets
    // scheduled on or after the day after it (maxAllowedDate), and on End's
    // own day, hardEndTime cuts off before its exact time instead of the
    // generic day-end hour.
    const endDate = scheduleOptions && scheduleOptions.endDate;
    const hardEndTime = endDate ? Common.LuxonDateFromJSDate(endDate) : null;
    const maxAllowedDate = hardEndTime ? hardEndTime.startOf("day").plus({ days: 1 }) : null;
    // Even distribution across the requested day range — without this, the
    // packing loop below only rolls over to the next day once the current
    // day's window is genuinely full, so a handful of places that comfortably
    // fit in one day never spread across the days the user explicitly asked
    // for (e.g. picking a 2-day range left everything on day 1, day 2 empty).
    // Forces a day break once a day's even share is placed — on every day
    // except the last one in the requested range, which takes whatever's
    // left so a skipped/closed place doesn't shrink the trip below the cap.
    // Same fix as mobile's ScheduleBuilder.js.
    const dayCap = maxAllowedDate ? maxAllowedDate.diff(dayDate.startOf("day"), "days").days : null;
    const targetPerDay = dayCap ? Math.ceil(ordered.length / dayCap) : null;
    let placedOnCurrentDay = 0;
    // "Leave room for lunch" — a real, visible placeholder event (no linked
    // Place, same convention TripAI.js's own AI-driven Lunch items use: a
    // Subject with no Places field) rather than a silent time-gap, so it's
    // something the user can actually see and later fill in with a real
    // restaurant. Inserted once per day, at the first moment the cursor
    // reaches noon — not tied to any place's location, so it adds no travel
    // time before/after (lastPlace is deliberately left untouched by this).
    const LUNCH_START_HOUR = 12;
    const LUNCH_DURATION_MINUTES = 60;
    const lunchPlacedDays = new Set();
    const maybeInsertLunch = (cursor) => {
      if (!scheduleOptions?.wantLunch) return cursor;
      const dayKey = cursor.toISODate();
      if (lunchPlacedDays.has(dayKey) || cursor.hour < LUNCH_START_HOUR) return cursor;
      const lunchEnd = cursor.plus({ minutes: LUNCH_DURATION_MINUTES });
      // No room left before the End time on this same day — skip rather
      // than schedule lunch right up against or past it.
      if (hardEndTime && cursor.hasSame(hardEndTime, "day") && lunchEnd > hardEndTime) return cursor;
      lunchPlacedDays.add(dayKey);
      let lunchEvent = new Appointment(window.guide_obj.schedule);
      lunchEvent.Subject = "Lunch";
      lunchEvent.Body = "";
      lunchEvent.Itinerary = scheduleName;
      lunchEvent.Start = cursor.toISO();
      lunchEvent.End = lunchEnd.toISO();
      window.guide_obj.schedule.addEvent(lunchEvent);
      return lunchEnd;
    };
    let placedCount = 0;
    // Actual place objects left out, not just a count — used in the
    // completion message below (see "Remove Unused Places" in this menu for
    // actually deleting them, on demand).
    const skippedPlaces = [];
    ordered.forEach((place) => {
      if (place === null) return;
      if (maxAllowedDate && dayDate.startOf("day") >= maxAllowedDate) {
        skippedPlaces.push(place);
        return;
      }
      // Force the day break here, before this day's window is actually full,
      // so places spread across the full requested range instead of piling
      // onto day 1. Skipped on the last day in range — plus({days:1})
      // reaching maxAllowedDate means there's no next day left to roll onto
      // — so it takes whatever's left rather than forcing an overflow that
      // would just get skipped by the check above.
      if (targetPerDay && placedOnCurrentDay >= targetPerDay && dayDate.startOf("day").plus({ days: 1 }) < maxAllowedDate) {
        dayDate = dayDate.startOf("day").plus({ days: 1 }).set({ hour: startH, minute: 0, second: 0, millisecond: 0 });
        thisDay = dayDate;
        firstEventOfDay = true;
        lastPlace = null;
        placedOnCurrentDay = 0;
      }
      dayDate = maybeInsertLunch(dayDate);
      // null means addEventOpeningHoursAware itself decided this place
      // isn't reachable at all — either every day it probed is genuinely
      // closed, or the first day within the trip's window that it's open
      // lands beyond maxAllowedDate — nothing was added to the schedule,
      // and the cursor/lastPlace are left exactly as they were.
      const result = addEventOpeningHoursAware(place, scheduleName, dayDate, lastPlace, rounding_to, firstEventOfDay, startH, endH, window.guide_obj.places, maxAllowedDate, scheduleOptions?.paceMultiplier, hardEndTime);
      if (result === null) {
        skippedPlaces.push(place);
        return;
      }
      dayDate = result;
      placedCount++;
      if (dayDate.day !== thisDay.day) {
        thisDay = dayDate;
        firstEventOfDay = true;
        placedOnCurrentDay = 1;
      } else {
        firstEventOfDay = false;
        placedOnCurrentDay++;
      }
      lastPlace = place;
    });

    // Auto-fix pass — push-pull (small same-day shift/shrink) first since
    // it's less disruptive, then swap for whatever's left (closed-all-day,
    // or a closes_during too big for push-pull). Requested 2026-09-03;
    // push-pull + closes_during widening added 2026-09-14.
    const _builtEvents = window.guide_obj.schedule.getEventsFromSchedule(scheduleName);
    Schedule.autoResolvePushPullConflicts(_builtEvents, window.guide_obj.places);
    Schedule.autoResolveClosedAllDayConflicts(_builtEvents, window.guide_obj.places);

    dispatch(slice1Actions.setScheduleName({ value: scheduleName }));
    dispatch(slice1Actions.setGuide({ guide: null, fitToBounds: true }));
    if (skippedPlaces.length > 0) {
      const skippedCount = skippedPlaces.length;
      // Never auto-deletes places anymore — a "Remove places that don't fit
      // schedule from guide" checkbox used to do this right after a build,
      // which was destructive if the user wanted to re-run the build with
      // different options (the just-deleted places could never be
      // reconsidered on a second attempt). Places left unscheduled now just
      // stay in the guide; "Remove Unused Places" in this menu handles
      // cleanup on demand, whenever the user actually wants it. Simplified
      // 2026-09-14.
      // TODO: once this (End-date cap) is a finalized, user-facing feature
      // rather than something being tried out, this should get its own
      // purpose-built dialog instead of reusing the generic myAlert.
      myAlert(
        `Scheduled ${placedCount} of ${placedCount + skippedCount} places by ${hardEndTime.toFormat("MMM d, yyyy HH:mm")}.` +
          ` The remaining ${skippedCount} ${skippedCount === 1 ? "wasn't" : "weren't"} added — try a later End date, add ${skippedCount === 1 ? "it" : "them"} manually, or use "Remove Unused Places" from this menu to clean up.`
      );
    }
    // The guide's Home/Itinerary document can reflect any schedule, not just
    // Main — same as the Schedule menu's own "Copy to Trip Itinerary" action,
    // which works on whichever schedule is currently selected. Defaults on
    // (scheduleOptions.updateItinerary !== false) so the itinerary doesn't
    // silently drift out of sync with the schedule it's supposed to reflect.
    if (scheduleOptions?.updateItinerary !== false) {
      const html_itin_String = window.guide_obj.createHTMLItinerary(scheduleName, GuideSettings.itinerary);
      const coverUrl = window.guide_obj.attributes.image_m;
      const isBadCoverUrl = (url) =>
        !url || url === "https://andiamotravelapp.com/Images/App/IllustratedImage.jpg" || /places\.googleapis\.com\/v1\/.*\/media/i.test(url);
      const coverImg = !isBadCoverUrl(coverUrl)
        ? `<p style="text-align:center;"><img src="${coverUrl}" style="max-width:100%;" alt=""></img></p>`
        : "";
      window.guide_obj.document = `${coverImg}<p><h3 style="text-align:center;">${window.guide_obj.attributes.guide_name}</h3></p></br>${html_itin_String}`;
      // Home caches its rendered document (state.homeScreen); without this a
      // guide whose Home still shows the "current guide format is Custom"
      // placeholder keeps showing it even though the itinerary was built —
      // BuildMyTripWizard.js resets it the same way.
      dispatch(slice1Actions.resetHomeScreen({ value: null }));
    }
    // enrichInfoWanted/enrichHoursWanted are now collected up front in
    // BuildScheduleDialog itself (folded in from a separate post-build
    // "Add more detail?" modal — that modal's native checkboxes inherited
    // Modal.css's global `input{width:70%}` rule same as BuildScheduleDialog's
    // own did, and rather than fix alignment twice, folding the two
    // checkboxes into the one dialog removes the second dialog entirely).
    runScheduleEnrichment(scheduleOptions?.enrichInfoWanted !== false, scheduleOptions?.enrichHoursWanted !== false);
  };

  // Reuses processInfoQueue/processOpeningHoursQueue (bounded-concurrency,
  // skip-if-already-enriched) above rather than a separate copy.
  const runScheduleEnrichment = (wantInfo, wantHours) => {
    const places = (window.guide_obj?.places?.placesList || []).slice();
    if ((!wantInfo && !wantHours) || places.length === 0) {
      goToSchedule();
      return;
    }
    if (wantInfo && wantHours) {
      processInfoQueue(places, () => processOpeningHoursQueue(places, goToSchedule, "Step 2/2"), "Step 1/2");
    } else if (wantInfo) {
      processInfoQueue(places, goToSchedule);
    } else {
      processOpeningHoursQueue(places, goToSchedule);
    }
  };

  // Mirrors BuildMyTripWizard.js's own goToSchedule — duplicated locally per the
  // existing convention (see goToMapSearch above) since that helper lives in
  // a sibling component.
  const goToSchedule = () => {
    // App.js only renders the Schedule screen/panel when GuidesOrPlaces is
    // "Places" (see App.js's CurrentAppScreen === "Schedule" && GuidesOrPlaces
    // === "Places" check) — required in both modes, not just desktop.
    if (GuidesOrPlaces !== "Places") {
      dispatch(slice1Actions.setGuidesOrPlaces({ value: "Places", GuideType: "Mine" }));
    }
    if (MobileMode) {
      dispatch(slice1Actions.setCurrentAppScreen({ value: "Schedule" }));
    } else {
      setTimeout(() => { window.location.href = "#schedule-start"; }, 350);
    }
  };

  const SortMenu = () => {
    //console.log("SortBy", SortBy);
    const menuList = [
      {
        label: "Name",
        method: handleSort,
        icon: <FontAwesomeIcon icon={solid("check")} />,
        checked: SortBy === SortByEnum.NAME,
        visible: true,
        info: "Sort the places based on the place name",
      },
      {
        label: "Category",
        method: handleSort,
        icon: <FontAwesomeIcon icon={solid("check")} />,
        checked: SortBy === SortByEnum.CATEGORY,
        visible: true,
        info: "Sort the places based on the place category name. Note: Places with multiple categories will appear multiple times.",
      },
      {
        label: "Group",
        method: handleSort,
        icon: <FontAwesomeIcon icon={solid("check")} />,
        checked: SortBy === SortByEnum.GROUP,
        visible: true,
        info: "Sort the places based on the place group name. Note: Places with multiple categories will appear multiple times.",
      },
      {
        label: "Rating",
        method: handleSort,
        icon: <FontAwesomeIcon icon={solid("check")} />,
        checked: SortBy === SortByEnum.RATING,
        visible: true,
        info: "Sort the places based on the place rating",
      },
      {
        label: "Distance",
        method: handleSort,
        icon: <FontAwesomeIcon icon={solid("check")} />,
        checked: SortBy === SortByEnum.DISTANCE,
        visible: true,
        info: "Sort the places based on the distance from the current selected place.",
      },
      {
        label: "Date",
        method: handleSort,
        icon: <FontAwesomeIcon icon={solid("check")} />,
        checked: SortBy === SortByEnum.DATE,
        visible: true,
        info: "Sort the places based on the date if they are in the schedule.",
      },
    ];
    //console.log("menuList", menuList);
    setMenuModalList(menuList);
    setMenuModalHeading("Sort Places");
    setMenuModalImage(<FontAwesomeIcon icon={solid("tasks")} />);
    setMenuModalPlace(null);
    setMenuModalShow(true);
    setMenuModalInfo("");
  };

  const myAlert = (message) => {
    setAlertModalTitle("Message");
    setAlertModalMessage(message);
    setAlertModalButtons([{ Title: "OK", Method: null }]);
    setAlertModalShow(true);
  };

  const UpdateDisplay = () => {
    if(GuideFormat === "Photo Mosaic"){
      let html = window.guide_obj.createHTMLItinerary("Main", GuideSettings.itinerary);
      dispatch(slice1Actions.clearHTMLStack());
      dispatch(slice1Actions.setHomeScreen({ value: html }));
    }
    dispatch(slice1Actions.setGuide({ guide: null }));
  };

  // AI Search wizard callbacks — mirrors Map.js's FindSearchResult/AddSearchResult/
  // updateUI/SetScheduleName (Map.js:1519-1529, 2415-2424). AISearch.js's single-question
  // flow silently falls through into its multi-step "find & add places" pipeline
  // whenever the AI's answer parses as a JSON array, so these props are required even
  // though only the single-question flow is intentionally exposed here.
  const FindSearchResult = (name, id) => {
    let index = -1;
    if (name != null) index = SearchResultsPlacesList.findIndex((x) => x.Name === name);
    else index = SearchResultsPlacesList.findIndex((x) => x.ID === id);
    if (index === -1) return null;
    return SearchResultsPlacesList[index];
  };

  const AddSearchResult = (place) => {
    dispatch(slice1Actions.addToSearchResultsPlacesList({ value: [place] }));
  };

  const updateUI = () => {
    dispatch(slice1Actions.setGuide({ guide: null, fitToBounds: true }));
  };

  const SetScheduleName = (scheduleName) => {
    dispatch(slice1Actions.ClearAllFilters());
    dispatch(slice1Actions.setScheduleName({ value: scheduleName }));
    dispatch(slice1Actions.SetSchedulesFilter({ value: [{ label: scheduleName }] }));
    dispatch(slice1Actions.setGuide({ guide: null }));
  };

  // Most of Map's own Search menu options (nearby, category, Wikipedia,
  // Flickr, trails) are anchored to the current map view, so they can't be
  // mirrored into Places the way AI Search was — instead, jump to Map and
  // open its Search menu there. Mirrors MobileBottomNavBar.js's
  // navigateAndOpenMenu("Map", "openMapSearchMenu"), which Map.js (Map.js:227-238)
  // already listens for via its command-palette entries; duplicated locally
  // since that helper lives in a sibling component.
  const goToMapSearch = () => {
    if (GuidesOrPlaces !== "Places") {
      dispatch(slice1Actions.setGuidesOrPlaces({ value: "Places", GuideType: "None" }));
    }
    dispatch(slice1Actions.setCurrentAppScreen({ value: "Map" }));
    setTimeout(() => window.dispatchEvent(new CustomEvent("openMapSearchMenu")), 350);
  };

  let number_of_places = 0;
  filtered_places.forEach((section) => {
    section.data.forEach((place_m) => {
      number_of_places++;
    });
  });

  if (SectionsDialogShow)
    return (
      <SectionsDialog
        show={SectionsDialogShow}
        onHide={() => setSectionsDialogShow(false)}
        update_display={null}
        mode={"select"}
        callback={selectSectionCallBack}
      />
    );
  return (
    <div>
      <div className="ph-toolbar">
        {/* Only shown after a menu/link action (PlaceModal/EventModal's
            "Center in Map", or Home.js's maplink) jumped straight to Map
            or Places in mobile mode, recording where it came from since
            CurrentAppScreen itself has no history. Pops one level of
            AppScreenStack per click, so a chain of jumps (e.g. schedulelink
            -> Schedule -> "Center in Map" -> Map) steps back through each
            one instead of losing the earlier hops. Found/fixed 2026-09-09
            as a single value, generalized to Places and to a real stack
            the same day. */}
        {MobileMode === true && (CurrentAppScreen === "Map" || CurrentAppScreen === "Places") && AppScreenStack.length > 0 && (
          <button
            className="ph-tool-btn"
            title="Back"
            onClick={() => {
              dispatch(slice1Actions.setCurrentAppScreen({ value: AppScreenStack[AppScreenStack.length - 1] }));
              dispatch(slice1Actions.popAppScreen());
            }}
          >
            <FontAwesomeIcon icon={solid("arrow-left")} />
          </button>
        )}
        <div className="ph-search">
          <FontAwesomeIcon icon={solid("magnifying-glass")} className="ph-search-icon" />
          <input type="text" value={SearchText} placeholder="Filter places" onChange={handleSearChanged} />
        </div>
        <span className="ph-count">{number_of_places}</span>
        <button className={`ph-tool-btn${!noFiltering ? " active" : ""}`} title="Filter" onClick={() => setShouldFilter(true)}>
          <FontAwesomeIcon icon={solid("filter")} />
        </button>
        {MobileMode === true &&
          CurrentAppScreen === "Map" &&
          (window.guide_obj?.schedule?.getEventsFromSchedule(ScheduleName) || []).length > 0 && (
            <>
              {(ReplayState === "Stopped" || ReplayState === "Paused") && (
                <button
                  className="ph-tool-btn"
                  title="Play itinerary"
                  onClick={() => dispatch(slice1Actions.setReplayState({ value: "Playing" }))}
                >
                  <FontAwesomeIcon icon={solid("play")} />
                </button>
              )}
              {ReplayState === "Playing" && (
                <button
                  className="ph-tool-btn"
                  title="Pause itinerary"
                  onClick={() => dispatch(slice1Actions.setReplayState({ value: "Paused" }))}
                >
                  <FontAwesomeIcon icon={solid("pause")} />
                </button>
              )}
              {(ReplayState === "Playing" || ReplayState === "Paused") && (
                <button
                  className="ph-tool-btn"
                  title="Stop itinerary"
                  onClick={() => dispatch(slice1Actions.setReplayState({ value: "Stopped" }))}
                >
                  <FontAwesomeIcon icon={solid("stop")} />
                </button>
              )}
            </>
          )}
        {(MobileMode === false || CurrentAppScreen === "Places") && (
          <button className="ph-tool-btn" title="Sort" onClick={() => SortMenu()}>
            <FontAwesomeIcon icon={solid("sort-alpha-down")} />
          </button>
        )}
        {GuideEditMode && (MobileMode === false || CurrentAppScreen === "Places") && (
          <button className="ph-tool-btn" title="Edit places" onClick={() => EditMenu()}>
            <FontAwesomeIcon icon={solid("ellipsis")} />
          </button>
        )}
      </div>
      {photoSearchLoading && (
        <div className="ph-loading">
          <Spinner animation="border" size="sm" variant="primary" />
          <span>Searching photos… {photoSearchProgress}</span>
        </div>
      )}
      {editPlaceShow ? (
        <PlaceEditModal
          show={editPlaceShow}
          onHide={() => setEditPlaceShow(false)}
          place={null}
          mode={"ADD"}
          type={"Icon"}
          groups={""}
          category={""}
          user_info={user_info}
          update_display={() => UpdateDisplay()}
        />
      ) : null}
      {infoSearchLoading && (
        <div className="ph-loading">
          <Spinner animation="border" size="sm" variant="primary" />
          <span>Finding info… {infoSearchProgress}</span>
        </div>
      )}
      {openingHoursSearchLoading && (
        <div className="ph-loading">
          <Spinner animation="border" size="sm" variant="primary" />
          <span>Finding opening times… {openingHoursSearchProgress}</span>
        </div>
      )}
      {countrySearchLoading && (
        <div className="ph-loading">
          <Spinner animation="border" size="sm" variant="primary" />
          <span>Setting place & country… {countrySearchProgress}</span>
        </div>
      )}
      {categorySearchLoading && (
        <div className="ph-loading">
          <Spinner animation="border" size="sm" variant="primary" />
          <span>Fixing categories… {categorySearchProgress}</span>
        </div>
      )}
      {MenuModalShow ? (
        <MenuModal
          show={MenuModalShow}
          onHide={() => { setMenuModalShow(false); setMenuModalBackFn(null); }}
          menu_modal_list={MenuModalList}
          menu_modal_heading={MenuModalHeading}
          menu_modal_image={MenuModalImage}
          menu_modal_place={MenuModalPlace}
          menu_modal_info={MenuModalInfo}
          onBack={MenuModalBackFn || undefined}
        />
      ) : null}

      <PromptModal
        show={PromptModalShow}
        onHide={() => setPromptModalShow(false)}
        PromptModalTitle={PromptModalTitle}
        PromptModalMessage={PromptModalMessage}
        PromptModalButtons={PromptModalButtons}
        PromptModalInputs={PromptModalInputs}
      />
      <AlertModal
        show={AlertModalShow}
        onHide={() => setAlertModalShow(false)}
        AlertModalTitle={AlertModalTitle}
        AlertModalMessage={AlertModalMessage}
        AlertModalButtons={AlertModalButtons}
      />
      <FilterModal show={shouldFilter} onHide={() => setShouldFilter(false)} />

      {/* Add Place dialog */}
      <Modal show={addPlaceShow} onHide={() => setAddPlaceShow(false)} centered dialogClassName="gc-add-dialog">
        <Modal.Body style={{ padding: 0 }}>
          <div className="gc-add-shell">
            <div className="gc-add-header">
              <span className="gc-add-title">Add Place</span>
              <button className="gc-close" onClick={() => setAddPlaceShow(false)}>
                <FontAwesomeIcon icon={solid("xmark")} />
              </button>
            </div>
            <div className="gc-add-body">
              <div className="gc-field">
                <label className="gc-label">Country</label>
                <select
                  className="gc-select"
                  value={addPlaceCountry}
                  onChange={(e) => {
                    // Blanking the city on an actual country change avoids
                    // leaving a stale city from the old country — city
                    // choices are scoped to a country.
                    setAddPlaceCountry(e.target.value);
                    setAddPlaceName("");
                    setAddPlaceCityMode("select");
                  }}
                >
                  {[...countries].sort().map((c, i) => (
                    <option key={i} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div className="gc-field">
                <label className="gc-label">City / Place Name</label>
                {(() => {
                  const cityList = (citiesByCountry[addPlaceCountry] || []).slice().sort((a, b) => b.popularity - a.popularity);
                  if (cityList.length > 0 && addPlaceCityMode === "select") {
                    return (
                      <select
                        className="gc-select"
                        value={addPlaceName}
                        onChange={(e) => {
                          if (e.target.value === "__other__") {
                            setAddPlaceCityMode("type");
                            setAddPlaceName("");
                          } else {
                            setAddPlaceName(e.target.value);
                          }
                        }}
                      >
                        <option value="" disabled>Select a city…</option>
                        {cityList.map((c) => (
                          <option key={c.city} value={c.city}>{c.city}</option>
                        ))}
                        <option value="__other__">Type a different city…</option>
                      </select>
                    );
                  }
                  // Free-text fallback — for a country with no curated cities,
                  // or the dropdown's own "type a different city" escape
                  // hatch. No geocode validation: this field also accepts
                  // non-geocodable entries like a neighborhood or region.
                  return (
                    <input
                      className="gc-input"
                      type="text"
                      value={addPlaceName}
                      onChange={(e) => setAddPlaceName(e.target.value)}
                      onKeyPress={(e) => { if (e.charCode === 13) saveNewPlace(); }}
                      autoFocus
                    />
                  );
                })()}
              </div>
              <div className="gc-toggle-row">
                <span className="gc-label">Visited</span>
                <span
                  className={`gc-toggle${addPlaceVisited ? " on" : ""}`}
                  onClick={() => setAddPlaceVisited(v => !v)}
                />
              </div>
            </div>
            {addPlaceError && <div className="gc-error-note" style={{ margin: "0 16px 8px" }}>{addPlaceError}</div>}
            <div className="gc-add-footer">
              <button className="gc-btn-cancel" onClick={() => setAddPlaceShow(false)} disabled={addPlaceSaving}>Cancel</button>
              <button className="gc-btn-save" onClick={saveNewPlace} disabled={addPlaceSaving}>{addPlaceSaving ? "Adding…" : "Add"}</button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Add Places (bulk) dialog */}
      <Modal show={addPlacesBulkShow} onHide={() => !addPlacesBulkProcessing && setAddPlacesBulkShow(false)} centered dialogClassName="gc-add-dialog">
        <Modal.Body style={{ padding: 0 }}>
          <div className="gc-add-shell">
            <div className="gc-add-header">
              <span className="gc-add-title">Add Places</span>
              <button className="gc-close" onClick={() => !addPlacesBulkProcessing && setAddPlacesBulkShow(false)}>
                <FontAwesomeIcon icon={solid("xmark")} />
              </button>
            </div>
            <div className="gc-add-body">
              <span className="gc-label">CSV — one place per line (semicolon-separated):</span>
              <div className="gc-example-box">
                <code className="gc-example-text">{"Paris; France\nColosseum; Rome; Italy\nTokyo; Japan"}</code>
              </div>
              <span className="gc-label" style={{ marginTop: "0.5rem", display: "block" }}>JSON array:</span>
              <div className="gc-example-box">
                <code className="gc-example-text">{'[\n  {"city": "Paris", "country": "France"},\n  {"name": "Colosseum", "city": "Rome", "country": "Italy"}\n]'}</code>
              </div>
              <span className="gc-textarea-hint" style={{ marginTop: "0.4rem", display: "block" }}>Country must match exactly (e.g. "United States")</span>
              <div className="gc-field" style={{ marginTop: "0.5rem" }}>
                <textarea
                  className="gc-textarea"
                  value={addPlacesBulkText}
                  onChange={(e) => setAddPlacesBulkText(e.target.value)}
                  placeholder="Paste CSV or JSON here…"
                  disabled={addPlacesBulkProcessing}
                  autoFocus
                />
                {addPlacesBulkErrorNote && <span className="gc-error-note">{addPlacesBulkErrorNote}</span>}
              </div>
              <div className="gc-field" style={{ marginTop: "0.6rem" }}>
                <label className="gc-label">Group Name (optional)</label>
                <input
                  className="gc-input"
                  type="text"
                  value={addPlacesBulkGroup}
                  onChange={(e) => setAddPlacesBulkGroup(e.target.value)}
                  placeholder="e.g. Japan Tour"
                  disabled={addPlacesBulkProcessing}
                />
              </div>
              <div className="gc-toggle-row">
                <span className="gc-label">Visited</span>
                <span
                  className={`gc-toggle${addPlacesBulkVisited ? " on" : ""}`}
                  onClick={() => !addPlacesBulkProcessing && setAddPlacesBulkVisited(v => !v)}
                />
              </div>
              {addPlacesBulkProcessing && (
                <div className="gc-processing">
                  <span className="gc-processing-dot" />
                  Geocoding places… {addPlacesBulkProgress}
                </div>
              )}
            </div>
            <div className="gc-add-footer">
              <button className="gc-btn-cancel" onClick={() => !addPlacesBulkProcessing && setAddPlacesBulkShow(false)} disabled={addPlacesBulkProcessing}>Cancel</button>
              <button className="gc-btn-save" onClick={saveBulkPlaces} disabled={addPlacesBulkProcessing || addPlacesBulkText.trim().length === 0}>Add</button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {GroupsDialogShow ? (
        <GroupsDialog
          show={GroupsDialogShow}
          onHide={() => setGroupsDialogShow(false)}
          update_display={UpdateDisplay}
        />
      ) : null}

      {CategoriesDialogShow ? (
        <CategoriesDialog
          show={CategoriesDialogShow}
          onHide={() => setCategoriesDialogShow(false)}
          update_display={UpdateDisplay}
        />
      ) : null}

      {AISearchShow ? (
        <AISearch
          show={AISearchShow}
          onHide={() => setAISearchShow(false)}
          user_info={user_info}
          update_ui={updateUI}
          set_schedule_name={SetScheduleName}
          find_search_result={FindSearchResult}
          add_search_result={AddSearchResult}
          questions={AppSettings.AISearch2}
          ai_categories={AppSettings.AICategories}
          ai_base_categories={AppSettings.AIBaseCategories}
          search_results_names={SearchResultsPlacesList.map((p) => p.Name)}
        />
      ) : null}

      <Modal show={personalizeShow} centered onHide={() => setPersonalizeShow(false)}>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem", fontWeight: 600 }}>Personalize Guide with Photos</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: "0.9rem", color: "#555", marginBottom: 16 }}>
            Select photos that have GPS data. Photos near an existing place can become its cover photo and/or be
            added to its notes; photos not near any place become new places. You'll be able to review and fine-tune
            everything, including the matching distance, before anything is applied.
          </p>
          <div>
            <label style={{ fontSize: "0.85rem", fontWeight: 500, display: "block", marginBottom: 6 }}>
              Photo source
            </label>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                className={"btn btn-sm " + (personalizeSource === "device" ? "btn-primary" : "btn-outline-secondary")}
                onClick={() => setPersonalizeSource("device")}
              >
                Cloud
              </button>
              <button
                className={"btn btn-sm " + (personalizeSource === "gdrive" ? "btn-primary" : "btn-outline-secondary")}
                onClick={() => setPersonalizeSource("gdrive")}
              >
                Google Drive
              </button>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: "1px solid #eee", padding: "10px 16px" }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setPersonalizeShow(false)}>
            Cancel
          </button>
          <button className="btn btn-primary btn-sm" onClick={_startPersonalizeGuide}>
            Select Photos
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={googleDrivePickerShow} centered size="lg" onHide={() => setGoogleDrivePickerShow(false)}>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem", fontWeight: 600 }}>Select Photos from Google Drive</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "12px 16px" }}>
          <GoogleDrivePicker
            fileType="image"
            multi={true}
            onSelect={(items) => {
              setGoogleDrivePickerShow(false);
              _personalizeGuideCallback(items);
            }}
          />
        </Modal.Body>
      </Modal>

      {ImageDialogShow ? (
        <ImageDialog
          show={ImageDialogShow}
          onHide={() => setImageDialogShow(false)}
          user_info={user_info}
          mode="select"
          modify_only="true"
          updateImage={_personalizeGuideCallback}
          size="sm"
          multi={true}
          exif={true}
          folder={null}
          setImageFolder={() => {}}
        />
      ) : null}

      <IdentifyPlacesModal show={identifyPlacesShow} onHide={() => setIdentifyPlacesShow(false)} onApplied={() => UpdateDisplay()} />

      <PersonalizeGuideReviewModal
        show={personalizeReviewShow}
        onHide={() => setPersonalizeReviewShow(false)}
        matchedItems={personalizeMatchedItems}
        newClusters={personalizeNewClusters}
        sensitivity={personalizeSensitivity}
        onSensitivityChange={_onPersonalizeSensitivityChange}
        maxSpan={personalizeMaxSpan}
        onMaxSpanChange={_onPersonalizeMaxSpanChange}
        minNewPlaceDistance={personalizeMinNewPlaceDistance}
        onMinNewPlaceDistanceChange={_onPersonalizeMinNewPlaceDistanceChange}
        unmatchedPlaces={personalizeUnmatchedPlaces}
        webExtras={personalizeWebExtras}
        onApply={_applyPersonalizeGuide}
      />

      <BuildScheduleDialog
        show={buildScheduleDialogShow}
        onHide={() => setBuildScheduleDialogShow(false)}
        onConfirm={(scheduleOptions) => buildScheduleFromGuidePlaces(buildSchedulePlacesRef.current, scheduleOptions)}
        scheduleNames={window.guide_obj?.schedule?.geScheduleNames() || []}
        defaultNewName={buildScheduleDefaultName}
        defaultStartDate={buildScheduleDefaultDate}
        defaultEndDate={buildScheduleDefaultEndDate}
        destinationSummary={buildScheduleDestinationSummary}
      />
    </div>
  );
};

export default PlacesHeader;
